import { m365AdminEn } from './m365-admin.en.js';
import { m365ChatEn, m365StepToolsEn } from './m365-chat.en.js';
import { m365FilesEn } from './m365-files.en.js';
import { m365ErrorsEn, m365UserEn } from './m365-user.en.js';
import type { Messages } from './tr.js';

/** English UI texts; must have exactly the same keys as tr.ts. */
export const en: Messages = {
  common: {
    yes: 'yes',
    no: 'no',
    optional: '(optional)',
    appName: 'Oppy',
    loading: 'Loading…',
    save: 'Save',
    saved: 'Saved.',
    cancel: 'Cancel',
    create: 'Create',
    delete: 'Delete',
    rename: 'Rename',
    back: 'Back',
    next: 'Continue',
    close: 'Close',
    retry: 'Try again',
    technicalDetails: 'Technical details',
    confirmDelete: 'Are you sure you want to delete this?',
    add: 'Add',
    you: 'you',
    requestId: 'Request ID',
    errorCode: 'Error code',
  },
  nav: {
    dashboard: 'Home',
    team: 'Team',
    settings: 'Settings',
    memory: 'Memory',
    skills: 'Skills',
    connectors: 'Connections',
    documents: 'Documents',
    logout: 'Log out',
    language: 'Language',
    organization: 'Company',
    admin: 'Administration',
    platform: 'Platform',
    mainNav: 'Main menu',
  },
  shell: {
    newConversation: 'New conversation',
    home: 'Home',
    skills: 'Skills',
    connections: 'Connections',
    documents: 'Documents',
    places: 'Pages',
    workspaces: 'Workspaces',
    newWorkspace: 'New workspace',
    allWorkspaces: 'View all',
    noWorkspaces: 'No workspaces yet.',
    codeBadge: 'Code',
    management: 'Management',
    access: 'Access & permissions',
    models: 'Models',
    integrations: 'Integrations',
    usage: 'Usage & limits',
    platform: 'Platform',
    history: 'Chat history',
    settings: 'Settings',
    settingsMenu: 'Settings menu',
    collapse: 'Collapse menu',
    expand: 'Expand menu',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    skip: 'Skip to content',
    experience: 'Choose a product',
    experienceHint: { chat: 'Chat', code: 'Code' },
    personalSpace: "{{name}}'s space",
    switchTenant: 'Switch company',
  },
  home: {
    domains: 'Your team workspaces',
    title: 'How can I help you?',
    titleCode: 'What shall we build today?',
    placeholder: 'Ask anything or add a document',
    placeholderCode: 'Give a task; plans, files and commands happen here',
    inputLabel: 'Your message',
    add: 'Add a file, skill or connection',
    attachFile: 'Add a file',
    useSkill: 'Use a skill',
    addConnection: 'Add a connection',
    files: 'Files to add',
    tooLarge: 'A file is over the {{mb}} MB limit.',
    defaultModel: 'Default model',
    in: 'Workspace:',
    inWorkspace: 'Starts in the {{name}} workspace',
    inPersonal: 'Starts in your personal space',
    personalName: 'Personal',
    change: 'change',
    noWorkspace: 'Create a chat space first to start a chat.',
    // D-152: in CompanyCode the first message opens the user's own code workspace
    personalCode: 'Your first message opens a code workspace of your own.',
    noModel: 'No model is available; ask your admin to set one up.',
    waiting_one: '{{count}} item is waiting for you',
    waiting_other: '{{count}} items are waiting for you',
    suggestionsLabel: 'Ideas to start with',
    discover: 'Discover skills and connections',
    suggestions: {
      chat: {
        contract: { label: 'Summarise the key clauses of this <b>contract</b> with page numbers', text: 'Summarise the key clauses of this contract with page numbers' },
        excel: { label: 'Make a report with charts from the October sales <b>Excel</b>', text: 'Make a report with charts from the October sales Excel' },
        web: { label: "Research our competitors' current prices on the <b>web</b>", text: "Research our competitors' current prices on the web" },
      },
      code: {
        tests: { label: 'Run the <b>tests</b> of this project and fix the failures', text: 'Run the tests of this project and fix the failures' },
        game: { label: 'Make a simple snake game and open it in the <b>preview</b>', text: 'Make a simple snake game and open it in the preview' },
        // v0.15a.3: where previews cannot open
        gameFiles: { label: 'Make a simple snake game as <b>one HTML</b> file', text: 'Make a simple snake game as one HTML file I can download and open in my browser' },
        readme: { label: 'Update the <b>README</b> to match the project', text: 'Update the README to match the project' },
      },
    },
  },
  workspaces: {
    filter: 'Workspace kind',
    filters: { all: 'All', chat: 'Chat', code: 'Code' },
    counts: {
      conversations_one: '{{count}} conversation',
      conversations_other: '{{count}} conversations',
      documents_one: '{{count}} document',
      documents_other: '{{count}} documents',
      projects_one: '{{count}} project',
      projects_other: '{{count}} projects',
      skills_one: '{{count}} skill',
      skills_other: '{{count}} skills',
    },
    members_one: '{{count}} member',
    members_other: '{{count}} members',
    newHint: 'For a team, a project or a topic',
    all: 'All',
    noneOfKind: 'No workspaces of this kind.',
    row: {
      plan: 'Plan step {{done}}/{{total}}',
      files_one: '{{count}} file',
      files_other: '{{count}} files',
      documents_one: '{{count}} document',
      documents_other: '{{count}} documents',
    },
    now: {
      label: 'Now',
      waiting: 'Waiting for you',
      running: 'Running',
      nothingWaits: 'Nothing is waiting for you.',
      nothingRuns: 'Nothing is running now.',
      questions_one: 'A question waits for your answer',
      questions_other: '{{count}} questions wait for your answer',
      approval: 'Waiting for approval: {{title}}',
      until: 'until {{time}}',
      progress: '{{done}} of {{total}} steps done',
    },
    intro: 'Conversations, documents and code projects are grouped here.',
    new: {
      title: 'Create workspace',
      kind: 'Kind',
      hint: { chat: 'Chat, documents, the web and files', code: 'Code, terminal, git and preview' },
    },
  },
  settingsDialog: {
    title: 'Settings',
    close: 'Close settings',
    sectionsLabel: 'Settings sections',
    openManagement: 'Open administration',
    sections: {
      general: 'General',
      account: 'Account',
      notifications: 'Notifications',
      memory: 'Memory',
      subscription: 'Subscription',
      organization: 'Company',
      limits: 'Limits & quotas',
      policies: 'Approval policies',
      email: 'Email',
      audit: 'Audit log',
      identity: 'Authentication',
    },
    general: {
      theme: 'Appearance',
      themes: { light: 'Light', dark: 'Dark', system: 'System' },
      language: 'Language',
      timeZoneBrowser: 'Taken from your browser',
      timeZoneChosen: 'Your choice',
      defaultModel: 'Default model',
      defaultCodeModel: 'Default model for code',
      orgChoice: "The company's choice",
      orgModel: "The company's choice ({{model}})",
      memoryHint: 'Oppy remembers something only when you ask. You can see and delete what it keeps under Memory.',
      data: 'My data',
      dataHint: 'Your chats, files and documents in this company',
      version: 'Oppy version',
      versionHint: 'The server release of this installation · {{vendor}}',
      download: 'Download',
    },
    account: { providers: 'My own providers' },
    notifications: {
      browser: 'Browser notifications',
      allow: 'Allow notifications',
      state: {
        default: 'Be told when something waits for your approval, even with this tab closed.',
        granted: 'On: items waiting for your approval are announced even with this tab closed.',
        denied: "Browser notifications are blocked. You can turn them on in the browser's site settings.",
        unsupported: 'This browser does not show notifications.',
      },
      badge: { granted: 'On', denied: 'Blocked', unsupported: 'Not supported', default: 'Off' },
      center: 'Notification centre',
      centerHint: 'The bell in the top bar: unread items show as a number.',
      what: 'What you are told about',
      kinds: {
        approval: 'Actions waiting for your approval',
        question: 'Questions the assistant asks you',
        task: 'Finished background work',
        failed: 'Answers that stopped or failed',
      },
    },
    email: {
      sent: 'Emails we send',
      kinds: { verify: 'Account verification', reset: 'Password reset', invite: 'Invitation to the company' },
      language: 'Email language',
      languageHint: 'Each person gets them in their own interface language.',
      budget: 'Budget alerts',
      budgetHint: 'One email to the admins at 80% and 100% of a budget.',
      soon: 'Coming soon',
    },
  },
  lang: { tr: 'Türkçe', en: 'English' },
  roles: {
    tenant: { owner: 'Super admin', admin: 'Admin', member: 'Member' },
    domainAdmin: 'Team admin',
    teams_one: '{{count}} team',
    teams_other: '{{count}} teams',
    workspace: { owner: 'Created by', editor: 'Editor', viewer: 'Viewer' },
  },
  kinds: { chat: 'Chat space', code: 'Code workspace', personal: 'Personal' },
  auth: {
    // Phase 13d: the sign-in pages are in English in both languages (GirisKurumsal.dc.html, GirisKurumsalHata.dc.html)
    directory: {
      title: 'Welcome to Oppy',
      subtitle: 'New here or coming back? Choose how you want to continue.',
      email: 'Email',
      password: 'Password',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      remember: 'Remember for 30 days',
      forgot: 'Forgot password',
      forgotNote: 'Your password is reset by your company’s IT team. After several wrong attempts you’ll be asked to wait a moment.',
      submit: 'Sign in',
      footer: 'Your password is checked by your company’s identity system. Oppy never stores it.',
      errors: {
        invalid: { title: 'Incorrect email or password.', action: 'Please try again.' },
        unavailable: { title: 'The sign-in service can’t be reached right now.', action: 'Please try again in a few minutes.' },
        locked: { title: 'Too many attempts.', action: 'Please wait a moment and try again.' },
        deactivated: { title: 'Your access to Oppy is turned off.', action: 'Please ask your administrator.' },
        relink: { title: 'Your company account has changed.', action: 'An administrator needs to confirm it before you can sign in. Please ask your administrator.' },
        check: { title: 'Please check what you entered.', action: 'Then try again.' },
        failed: { title: 'Sign-in didn’t work.', action: 'Please reload the page and try again.' },
      },
      // Phase 13d-2 (D-114): how long the wait after failed attempts still lasts
      wait_minutes_one: 'Try again in {{count}} minute.',
      wait_minutes_other: 'Try again in {{count}} minutes.',
      wait_seconds_one: 'Try again in {{count}} second.',
      wait_seconds_other: 'Try again in {{count}} seconds.',
      waitOver: 'You can try again now.',
      countdown: 'Sign in ({{time}})',
    },
    breakGlass: {
      title: 'Emergency sign-in',
      subtitle: 'For the break-glass admin, when the company sign-in can’t be used.',
      code: 'Authentication code',
      codeHint: 'The 6 digits your authenticator app shows.',
      codeInvalid: 'Enter the 6-digit code from your authenticator app.',
      footer: 'Every emergency sign-in is recorded in the audit log.',
      errors: {
        invalid: { title: 'Incorrect email, password or code.', action: 'Please try again.' },
        unavailable: { title: 'Oppy can’t be reached right now.', action: 'Please try again in a few minutes.' },
        locked: { title: 'Too many attempts.', action: 'Please wait a moment and try again.' },
        deactivated: { title: 'This account is turned off.', action: 'Please ask another super admin.' },
        relink: { title: 'This account cannot sign in here.', action: 'Please ask another super admin.' },
        check: { title: 'Please check what you entered.', action: 'Then try again.' },
        failed: { title: 'Sign-in didn’t work.', action: 'Please reload the page and try again.' },
      },
    },
    email: 'Email',
    password: 'Password',
    login: {
      title: 'Sign in',
      submit: 'Sign in',
      noAccount: 'No account yet?',
      signupLink: 'Sign up',
      forgot: 'Forgot password',
    },
    signup: {
      title: 'Create account',
      name: 'Your name',
      passwordHint: 'At least 10 characters. Common passwords are not accepted.',
      terms: 'I accept the terms of use and the privacy notice.',
      termsRequired: 'Accept the terms to continue.',
      submit: 'Create account',
      haveAccount: 'Already have an account?',
      loginLink: 'Sign in',
    },
    checkEmail: {
      title: 'Check your email',
      body: 'We sent a confirmation link to {{email}}. The link is valid for 24 hours.',
      bodyGeneric: 'We sent a confirmation link to your email address. The link is valid for 24 hours.',
      hint: 'If nothing arrives, check your spam folder. If you already have an account with this address, sign in or reset your password.',
      loginCta: 'Go to sign in',
    },
    verify: {
      title: 'Email confirmation',
      working: 'Confirming…',
      success: 'Your email address is confirmed.',
      continue: 'Continue',
      loginCta: 'Sign in',
      missingToken: 'The link has no confirmation code. Open the link from the email in full.',
    },
    forgot: {
      title: 'Forgot your password?',
      body: 'Enter your email; if you have an account, we will send a reset link.',
      submit: 'Send link',
      sent: 'If an account exists for this address, we sent a reset link. The link is valid for 30 minutes.',
    },
    reset: {
      title: 'Choose a new password',
      password: 'New password',
      confirm: 'New password (again)',
      mismatch: 'The passwords do not match.',
      submit: 'Change password',
      success: 'Your password has changed. For your security, every session on every device was signed out.',
      loginCta: 'Sign in',
      missingToken: 'The link has no reset code. Open the link from the email in full.',
    },
    invite: {
      title: 'Team invitation',
      body: '“{{tenant}}” invited you as {{role}}.',
      forEmail: 'The invitation was sent to: {{email}}',
      needLogin: 'Sign in or create an account with this address to accept the invitation.',
      accept: 'Accept invitation',
      accepted: 'You joined the team.',
      alreadyMember: 'You are already a member of this team.',
      missingToken: 'The link has no invitation code. Open the link from the email in full.',
    },
    footer: 'Oppy · {{chatProduct}} and {{codeProduct}}',
  },
  verifyBanner: {
    text: 'Your email address is not confirmed yet. You can chat; sending invitations and external connections need a confirmed address.',
    resend: 'Send the link again',
    sent: 'A new confirmation link was sent.',
  },
  dashboard: {
    greeting: 'Hello, {{name}}',
    workspaces: {
      title: 'Workspaces',
      newChat: 'New chat space',
      newCode: 'New code workspace',
      name: 'Name',
      empty: 'No workspaces yet.',
    },
    recent: { title: 'Recent conversations', empty: 'No conversations yet.' },
    activity: {
      title: 'Right now',
      waiting: 'Waiting for you',
      running: 'Running',
      tasks: '{{count}} background tasks are running.',
      kinds: { tool: 'Needs approval', plan: 'Plan needs approval', question: 'Question waiting' },
      status: { queued: 'Queued', running: 'Running', waiting_approval: 'Needs approval', waiting_input: 'Needs an answer', waiting_children: 'Waiting for helpers' },
    },
    projects: { title: 'Projects', empty: 'No projects yet.' },
  },
  workspace: {
    tabsLabel: 'Workspace sections',
    tabs: { conversations: 'Conversations', projects: 'Projects', environment: 'Environment', documents: 'Documents', skills: 'Skills', members: 'Members', settings: 'Settings' },
    documents: {
      title: 'Workspace documents',
      all: 'All ({{count}})',
      add: 'Add a document or drop it here',
      uploading: 'Uploading…',
      intro: "Documents the workspace's members can use as sources in their conversations.",
      empty: 'No documents yet',
      emptyHint: "Contracts, price lists, reports… Documents you add are read and become sources in this workspace's conversations.",
    },
    invite: {
      button: 'Invite',
      title: 'Invite to {{name}}',
      add: 'Add',
      search: 'Search the company',
      person: 'Person',
      editorHint: 'Starts conversations, adds documents and skills',
      viewerHint: 'Can only look',
      nobody: 'Nobody to add',
      nobodyHint: 'Everyone in your company is in this workspace. Invite someone new to the company first.',
      toOrganization: 'Invite to the company',
    },
    skills: {
      intro: 'Skills that can be used in this workspace; turning them on or off applies here only.',
      empty: 'No skills of this workspace',
      emptyHint: "Add a skill here when only this workspace's members should use it.",
      own: "This workspace's skills",
      others: 'Other skills',
      toggle: 'Turn {{name}} on or off in this workspace',
    },
    settings: {
      name: 'Name',
      directoryName: 'Your company’s admins name this shared workspace under Management → Authentication.',
      description: 'Description',
      descriptionHint: 'Shown under the name.',
      saved: 'Saved',
      deleteTitle: 'Delete the workspace',
      deleteHint: 'The workspace is deleted with its conversations, projects and documents.',
      delete: 'Delete the workspace',
      deleteConfirmTitle: 'Delete {{name}}?',
    },
    actions: 'Workspace actions',
    deleteConfirm: 'Its conversations, projects and documents will no longer be visible.',
    conversations: {
      title: 'Conversations',
      new: 'New conversation',
      empty: 'No conversations yet',
      emptyHint: 'Ask a question or add a document; your conversations in this workspace gather here. Conversations are visible to you only.',
      search: 'Search conversations in this workspace',
      allProjects: 'All projects',
    },
    projects: {
      codeHint: 'Projects are folders in your code; a conversation starts in a folder.',
      emptyHint: 'Projects gather conversations and documents around one piece of work.',
      emptyShort: 'No projects yet.',
      title: 'Projects',
      new: 'New project',
      name: 'Project name',
      rootPath: 'Folder',
      rootPathHint: 'Folder in the workspace (e.g. web or services/api). Left empty, it is derived from the name. Nested folders cannot be separate projects.',
      chatHint: 'Projects group conversations and documents.',
      empty: 'No projects yet.',
    },
    members: {
      intro: "Members share the workspace's documents and skills; conversations are private.",
      removeTitle: 'Remove {{name}} from the workspace?',
      removeBody: "They lose the workspace's documents and skills; their own conversations stay theirs.",
      title: 'Members',
      add: 'Add member',
      person: 'Person',
      role: 'Role',
      remove: 'Remove',
      noCandidates: 'No other team members to add.',
    },
  },
  conversation: {
    // D-152
    personalWorkspace: 'Your own code workspace is open.',
    options: 'Conversation options: thinking, skills, export',
    placeholderQuestion: 'Answer the question above or type here…',
    untitled: 'Untitled conversation',
    placeholder: 'Write your message…',
    send: 'Send',
    empty: 'No messages yet. Write something to start.',
    you: 'You',
    assistant: 'Oppy',
    streaming: 'Writing a reply…',
    connected: 'Connected',
    reconnecting: 'Connection lost, reconnecting…',
    turnFailed: 'No reply could be produced. Try again.',
    turnCancelled: 'The reply was stopped.',
    backToWorkspace: 'Back to the workspace',
    attach: 'Attach files',
    attaching: 'Uploading…',
    removeAttachment: 'Remove attachment',
    attachmentLimit: 'Files can be at most {{mb}} MB.',
    dropHere: 'Drop the files here',
    menu: 'Conversation menu',
    tool: {
      running: 'Running',
      done: 'Done',
      failed: 'Did not finish',
      details: 'Technical details',
      output: 'Output',
      result: 'Result',
      seconds: '{{seconds}} s',
    },
    artifact: {
      download: 'Download',
      preview: 'Preview',
      version: 'version {{version}}',
      kinds: {
        image: 'Image',
        document: 'Word document',
        spreadsheet: 'Excel sheet',
        presentation: 'Presentation',
        pdf: 'PDF',
        csv: 'CSV',
        html: 'Web page',
        markdown: 'Text',
        text: 'Text',
        archive: 'Archive',
        other: 'File',
      },
    },
    queue: 'Add to the queue',
    emptyCode: 'Give a task: plans, files and commands show up here.',
    answerReady: 'The answer is ready.',
    toBottom: 'Jump to the latest',
    connecting: 'Connecting…',
    turnsLabel: 'The conversation’s messages',
  },
  agent: {
    continue: 'Continue',
    stop: 'Stop',
    queued: 'Queued',
    reasoning: 'Reasoning',
    status: {
      waiting: 'Waiting for your approval',
      retry: 'The model did not answer; retrying ({{attempt}}/{{max}})…',
      compacting: 'Summarising the conversation…',
      retryIn: '(next try in {{seconds}} s; you can stop and pick another model)',
      thinking: 'Thinking…',
    },
    approval: {
      waiting: 'Action waiting for approval',
      explain: 'Explain',
      fromHelper: 'A helper asks: {{description}}',
      approved: 'approved ({{option}})',
      answered: 'answered',
      rejected: 'you rejected it',
      expired: 'time ran out; the agent went on without an answer',
      cancelled: 'cancelled',
      pending: 'waiting',
    },
    sent: {
      source: 'Connection: {{name}} · tool: {{tool}}',
      intro: 'Oppy will send:',
      introRemember: 'Oppy will keep this in its memory:',
      introOwn: 'Oppy will use:',
      raw: 'Show raw data',
      more: 'Show all',
      less: 'Show less',
      empty: 'empty',
      fields_one: '{{count}} field',
      fields_other: '{{count}} fields',
      cut_one: '… {{chars}} more character (not shown)',
      cut_other: '… {{chars}} more characters (not shown)',
      truncated: 'What will be sent is very long: the card shows {{shown}} of {{total}} characters. Each shortened text ends with how much is not shown. Keep this in mind before you allow it.',
    },
    question: {
      heading_one: 'I have a question',
      heading_other: 'I have {{count}} questions',
      intro: 'Answer and I will go on right away. Leave blank what you do not know.',
      otherPlaceholder: 'Or write your own answer',
      title: 'Question',
      other: 'Your own answer',
      submit: 'Answer',
      skip: 'Skip',
      expires: 'If there is no answer by {{time}}, it goes on with the questions left blank',
    },
    plan: {
      title: 'Plan',
      keptPlanning: 'kept planning',
      edit: 'Edit',
      preview: 'Preview',
      feedback: 'Add a note (sent to the agent if you keep planning)',
      approved: 'Plan approved',
      approvedEdited: 'Plan approved with edits',
    },
    todos: {
      progress: 'Plan progress',
      running: 'in progress',
      title: 'Tasks ({{done}}/{{total}})',
      waits: '(waits for {{ids}})',
      step: 'Step {{current}} of {{total}}',
    },
    helper: {
      group: '{{count}} helpers work together',
      background: 'In the background',
      usage: '{{tokens}} tokens · {{calls}} model calls · {{tools}} tools · {{seconds}} s',
      cost: 'cost {{cost}}',
      steps: '{{count}} steps',
      result: 'Result',
      status: { running: 'Working', completed: 'Done', failed: 'Not finished', cancelled: 'Stopped', interrupted: 'Interrupted' },
    },
    tasks: {
      title: 'Background tasks ({{running}} running of {{total}})',
      stop: 'Stop',
      status: { running: 'Running', completed: 'Done', failed: 'Failed', cancelled: 'Stopped', stopped_by_restart: 'Stopped by a restart' },
    },
    mode: {
      fullTitle: 'Turn on full access?',
      fullOk: 'Turn on full access',
      label: 'Approval mode',
      chip: 'Mode: {{mode}}',
      ask: 'Ask',
      auto_edit: 'Allow edits',
      plan: 'Plan',
      full: 'Full access',
      hints: {
        ask: 'You are asked before anything changes files or runs a command.',
        auto_edit: 'File edits run without asking; commands are still asked.',
        plan: 'The agent only reads and prepares a plan; nothing changes until you approve.',
        full: 'The agent runs commands and changes files without asking.',
      },
      fullConfirm: 'With full access the agent runs commands and changes files without asking.',
    },
    notice: {
      failed: 'No answer could be produced. Try again.',
      cancelled: 'You stopped it. Written files and the answer so far stay.',
      rejected: 'You rejected it; the agent did not do it.',
      interrupted: 'The run was interrupted; the result of one action is unknown.',
      run_interrupted: 'The server restarted; the run resumes where it stopped…',
      run_resumed: 'The run continues where it stopped.',
      limit: {
        tool_calls: 'This turn reached its limit of {{max}} tool calls (loop protection).',
        steps: 'This turn reached its limit of {{max}} steps.',
        repeat: 'The agent repeated the same action; it was stopped.',
      },
    },
    notification: 'A background task finished: {{label}}',
    notify: {
      enable: 'Turn on notifications',
      question: 'Oppy has a question for you',
      approval: 'Oppy is waiting for your approval',
    },
    preview: { autoRefresh: 'Reload when files change' },
    rules: {
      title: 'Permission rules',
      hint: 'Actions you allowed with "always allow" on a card. Remove one and you are asked again.',
      empty: 'No lasting permissions yet.',
      revoke: 'Remove',
      scopes: { user: 'All my workspaces', workspace: 'One workspace', conversation: 'One conversation' },
    },
    fullAccess: {
      title: 'Full access',
      hint: 'When on, members may choose full access in trusted code workspaces; the agent then runs commands without asking.',
      enable: 'Allow full access',
    },
  },
  settings: {
    defaultModel: {
      title: 'My default model',
      hint: "Your new conversations start with this model. Leave it empty to use the company's choice.",
    },
    providers: { hint: 'Only you see and use your own providers; keys are stored encrypted and never shown again.' },
    title: 'Settings',
    profile: {
      title: 'Profile',
      name: 'Your name',
      language: 'Interface language',
      userType: 'How you use it',
      business: 'Documents and research',
      developer: 'Code and projects',
    },
    email: { title: 'Email', verified: 'Confirmed', unverified: 'Not confirmed' },
    password: {
      title: 'Password',
      directory: 'Your company’s IT team manages your password; Oppy never sees or stores it.',
      current: 'Current password',
      next: 'New password',
      submit: 'Change password',
      success: 'Your password has changed. Sessions on other devices were signed out.',
    },
    sessions: {
      title: 'Open sessions',
      current: 'This device',
      lastUsed: 'Last used: {{date}}',
      revoke: 'Sign out session',
      unknownDevice: 'Unknown device',
      app: 'Oppy app',
    },
    answers: {
      title: 'Answers',
      language: 'Answer language',
      languageHint: 'Automatic answers in the language of your message.',
      auto: 'Automatic',
      instructions: 'Your instructions for every conversation',
      instructionsHint: 'E.g. Keep it short, show numbers in tables. At most 4000 characters.',
      timeZone: 'Time zone',
      timeZoneHint: "The assistant sees today's date and the time of your messages in this zone. The company's default: {{zone}}.",
      timeZoneAuto: 'From the browser ({{zone}})',
    },
    data: {
      title: 'Your data',
      exportHint: 'Download all your conversations, memory and preferences in this company as one file.',
      export: 'Download my data',
      deleteTitle: 'Delete my data',
      deleteHint: 'All your conversations in this company, the files you uploaded and the files produced, your environments, memory and notifications are deleted for good; your Microsoft connection, if any, is removed too. Your account and the team’s shared workspaces stay.',
      deleted: '{{conversations}} conversations, {{memories}} memory entries and {{documents}} documents were deleted.',
      confirm: 'I understand this cannot be undone.',
      delete: 'Delete my data for good',
      email: 'Your e-mail address',
      emailHint: 'Your company account has no password here. To confirm, type your account’s e-mail address: {{email}}',
    },
  },
  // Phase 13d: Management → "Authentication"
  identity: {
    none: 'This edition doesn’t sign people in through a company authentication service.',
    connection: {
      title: 'Connection',
      hint: 'The company’s authentication service. These values come from the server’s configuration and can’t be changed here.',
      kind: 'Kind',
      companyAuth: 'Company authentication service (Active Directory, Keycloak)',
      address: 'Address',
      issuer: 'Expected issuer (iss)',
      audience: 'Expected client (aud, azp)',
      unchecked: 'not checked',
      ownerRole: 'Super admin role',
      adminRole: 'Team admin role',
      roleFields: 'Fields roles are read from',
      domainFields: 'Fields domains are read from',
      prefix: 'prefix: {{prefix}}',
      fallback: 'First-installation fallback',
      fallbackOn: 'On: OPPY_ADMIN_USERNAMES ({{count}})',
      fallbackOff: 'Off',
      test: 'Test the connection',
      result: { reachable: 'The service answers', unexpected: 'Unexpected answer', unreachable: 'The service can’t be reached' },
      answer: 'HTTP {{status}}, {{ms}} ms',
      noAnswer: 'no answer within {{ms}} ms',
    },
    domains: {
      title: 'Domains',
      hint: 'The teams of the company directory. Each domain has a shared {{chatProduct}} workspace; you name it here. Membership comes from the directory.',
      empty: 'No domains yet. A domain shows here once one of its people signs in.',
      domain: 'Domain',
      workspace: 'Workspace name',
      members: 'Members',
      admins: 'Admins',
      displayName: 'Name of the {{domain}} workspace',
    },
  },
  team: {
    handover: {
      text: 'Before {{name}} is removed, who receives their workspaces and the files in them?',
      leaveText: 'Before you leave, who receives your workspaces and the files in them?',
      to: 'Receiver',
      private: 'Private conversations are not handed over: they go to the trash and are deleted when its retention ends.',
      copies: "If the receiver has their own copy of a code workspace, the leaver's copy there is deleted.",
    },
    title: 'Team',
    name: 'Company name',
    nameSuperAdminOnly: 'Only the super admin can rename the company.',
    members: { title: 'Members', remove: 'Remove', leave: 'Leave', role: 'Role', directory: 'Company account', deactivated: 'Access off', deactivate: 'Turn off access', reactivate: 'Turn on access' },
    directoryHint: 'People join the company the first time they sign in with their company account. Their roles and domains come from the company directory; there are no invitations.',
    invite: {
      title: 'Invite',
      role: 'Role',
      submit: 'Send invitation',
      sent: 'Invitation sent.',
      needVerified: 'Confirm your email address before sending invitations.',
      onlyAdmins: 'Only super admins and admins can invite members.',
    },
    invites: {
      title: 'Pending invitations',
      expires: 'Expires: {{date}}',
      revoke: 'Revoke',
      empty: 'No pending invitations.',
    },
    switcher: 'Switch company',
    switches: {
      title: 'Company settings',
      memory: 'Allow memory',
      memoryHint: 'When off, nobody’s memory goes into answers and nothing new is saved.',
      codeApproval: 'Ask before code runs in chats',
      codeApprovalHint: 'When off, code runs without asking in the chat’s private space without internet; the user sees what is done.',
      trash: 'Trash retention (days)',
      trashHint: 'Empty means 30 days.',
      timeZone: 'Company time zone',
      timeZoneHint: 'The company’s day turns at 00:00 in this zone: daily budgets reset then, and “Today” and “N days ago” count by it. People whose browser does not tell its zone and who chose none use it too.',
    },
    feedback: {
      title: 'Answer ratings',
      hint: 'Last 30 days; likes per model. Conversation contents are never shown.',
      empty: 'No ratings yet.',
      notes: '{{count}} notes',
      columns: { model: 'Model', up: 'Likes', down: 'Dislikes', notes: 'With a note' },
    },
  },
  sandbox: {
    title: 'Environment',
    states: { running: 'Running', idle: 'Idle', suspended: 'Suspended', missing: 'Not started yet' },
    start: 'Start',
    suspend: 'Suspend',
    isolated: 'Private, isolated environment. Other members use their own.',
    tabs: { files: 'Files', terminal: 'Terminal', git: 'Git', preview: 'Preview' },
    files: {
      root: 'Workspace',
      up: 'Parent folder',
      empty: 'This folder is empty.',
      hiddenIgnored: '{{count}} items hidden by .gitignore.',
      upload: 'Upload a file',
      download: 'Download',
      delete: 'Move to trash',
      zip: 'Download as ZIP',
      trash: 'Trash',
      trashEmpty: 'The trash is empty.',
      restore: 'Restore',
      close: 'Close',
      truncated: 'Only part of the file is shown.',
    },
    terminal: {
      connect: 'Open terminal',
      disconnect: 'Close',
      closed: 'The terminal closed.',
      hint: 'The shell opens in the workspace folder; platform keys are not in this environment.',
    },
    git: {
      notRepo: 'This workspace has no Git repository.',
      init: 'Create a Git repository',
      branch: 'Branch: {{branch}}',
      clean: 'No changes.',
      message: 'Commit message',
      commit: 'Commit',
      selectAll: 'Select all',
      diff: 'Show diff',
      committed: 'Commit created.',
    },
    preview: {
      ports: 'Running applications',
      noPorts: 'No port is listening. Your app shows up here once it runs.',
      refresh: 'Refresh',
      open: 'Open preview',
      newTab: 'Open in a new tab',
      reload: 'Reload page',
      frameTitle: 'Application preview',
      // v0.15a.3: an installation reached by its IP address
      offTitle: 'Preview is off on this installation',
      offBody: 'It opens once a domain name is connected. Your files are in the Files tab, where you can open or download them.',
      offFiles: 'Files',
    },
    panel: 'Workspace panel',
    tabsLabel: 'Panel tabs',
    offTitle: 'The environment is off',
  },
  // D-99: CompanyCode's panel next to a conversation (KodPanel.dc.html, KodPanelKapali.dc.html)
  codePanel: {
    hide: 'Hide the panel',
    show: 'Show the panel',
    showRunning: 'Show the panel · the workspace is running',
    showRunningLabel: 'Show the panel. The workspace is running',
    width: 'Panel width',
    openFile: 'Open in the panel: {{path}}',
    previewReady: 'An app is running on port {{port}}',
    openPreview: 'Open in preview',
    previewOff: 'An app is running on port {{port}}; preview is off on this installation',
    showFiles: 'Show files',
  },
  experience: {
    chat: '{{chatProduct}}',
    code: '{{codeProduct}}',
    toCode: 'Switch to {{codeProduct}}',
    toChat: 'Switch to {{chatProduct}}',
  },
  notifications: {
    title: 'Notifications',
    readAll: 'Mark all as read',
    empty: 'No notifications.',
    kinds: {
      approval: 'Needs approval',
      question: 'Question',
      task: 'Background',
      run_failed: 'No answer',
      run_interrupted: 'Interrupted',
      license: 'License',
    },
    titleUnread_one: 'Notifications: {{count}} unread',
    titleUnread_other: 'Notifications: {{count}} unread',
  },
  sessions: {
    closeSearch: 'Close the search',
    viewAll: 'See all',
    historyTitle: 'Conversation history',
    purgeTitle: 'Delete the conversation for good?',
    day: { today: 'Today', yesterday: 'Yesterday', week: 'Last 7 days', month: 'Last 30 days', older: 'Older' },
    title: 'Conversations',
    actions: 'Conversation actions',
    restore: 'Restore',
    purge: 'Delete forever',
    purgeConfirm: 'It is deleted with its files and the files it produced. This cannot be undone.',
    pin: 'Pin',
    unpin: 'Unpin',
    pinned: 'Pinned',
    group: 'Group',
    noGroup: 'No group',
    archive: 'Archive',
    unarchive: 'Unarchive',
    toTrash: 'Move to trash',
    allGroups: 'All',
    newGroup: 'New group',
    deleteGroup: 'Delete the group (conversations stay)',
    newChat: 'New chat',
    newCode: 'New code conversation',
    search: 'Search conversations',
    noResults: 'No matching conversation.',
    views: 'View',
    view: { active: 'Active', archived: 'Archive', trash: 'Trash' },
    trashHint: 'Conversations in the trash are deleted on their own when the retention ends.',
    emptyTrash: 'Empty the trash',
    emptyTrashConfirm: 'Every conversation in the trash is deleted for good. This cannot be undone.',
    pinnedTitle: 'Pinned',
    recentTitle: 'Recent',
    empty: { active: 'No conversations yet.', archived: 'Nothing archived.', trash: 'The trash is empty.' },
    more: 'Show more',
    count: '{{count}} conversations',
    options: 'History options',
  },
  status: { answer: 'Waiting for your answer', approval: 'Waiting for your approval', running: 'Working' },
  time: { now: 'just now', minutes: '{{count}} min ago', hours: '{{count}} h ago', today: 'Today {{time}}', yesterday: 'Yesterday', yesterdayAt: 'Yesterday {{time}}' },
  attach: {
    label: 'Attachments',
    uploading: 'Uploading',
    reading: 'Reading',
    ready: 'Ready',
    readyWith: 'Ready · {{size}}',
    ocr: 'text recognized from images',
    failed: 'Upload failed',
    unreadable: 'Could not be read: {{reason}}',
    duplicate: 'This file is already attached',
    remove: 'Remove {{name}}',
  },
  steps: {
    label: 'Steps',
    allowedBy: {
      trusted_read: 'trusted connection, read-only: not asked',
      tool_policy: 'allowed by the admin: not asked',
      rule: 'by your lasting permission: not asked',
      full_access: 'full access: not asked',
    },
    show: 'Show the steps',
    hide: 'Hide the steps',
    outcome: {
      prepared: 'Prepared in {{time}}',
      preparedNoTime: 'Prepared',
      stopped: 'Stopped · {{time}}',
      stoppedNoTime: 'Stopped',
      budget: 'Stopped: the budget ran out · {{time}}',
      budgetNoTime: 'Stopped: the budget ran out',
      allowance: 'Waiting for the daily allowance · {{time}}',
      allowanceNoTime: 'Waiting for the daily allowance',
      failed: 'Did not finish · {{time}}',
      failedNoTime: 'Did not finish',
      ended: 'Ended · {{time}}',
      endedNoTime: 'Ended',
    },
    manySkills: 'This turn loaded {{count}} skills; skills it does not need make the answer slower.',
    docWaitFailed: 'Could not read the document: {{names}}',
    skill: {
      byUser: 'You ran it with /',
      reused: 'Already loaded; not added again',
      part: 'Part {{part}}/{{parts}}',
      grants: 'Runs without asking this turn: {{list}}',
      ownScripts: "the skill's own scripts",
    },
    think: { examine: 'Examined the question', evaluate: 'Evaluated the findings' },
    answer: 'Wrote the answer',
    failed: 'did not finish',
    results: 'Search results',
    moreResults: '+{{count}} results',
    thought: 'Thought',
    current: {
      waitingAnswer: 'Waiting for your answer',
      examine: 'Examining the question',
      evaluate: 'Evaluating the findings',
      writing: 'Writing the answer',
      waiting: 'Waiting for your approval',
      compacting: 'Summarising the conversation',
      retry: 'Trying again ({{attempt}}/{{max}})',
    },
    parts: {
      skills_one: '{{count}} skill',
      skills_other: '{{count}} skills',
      docs_one: '{{count}} document search',
      docs_other: '{{count}} document searches',
      searches_one: '{{count}} search',
      searches_other: '{{count}} searches',
      pages_one: '{{count}} page',
      pages_other: '{{count}} pages',
      files_one: '{{count}} file',
      files_other: '{{count}} files',
      commands_one: '{{count}} command',
      commands_other: '{{count}} commands',
      other_one: '{{count}} action',
      other_other: '{{count}} actions',
    },
    tools: {
      web_search: { running: 'Searching the web:', done: 'Searched the web:' },
      web_fetch: { running: 'Reading:', done: 'Read:' },
      doc_search: { running: 'Searching the documents:', done: 'Searched the documents:' },
      doc_read: { running: 'Reading the document:', done: 'Read the document:' },
      skill: { running: 'Loading the skill:', done: 'Loaded the skill:' },
      doc_wait: { running: 'Reading the document:', done: 'Read the document:' },
      skill_search: { running: 'Finding skills:', done: 'Found skills:' },
      agent: { running: 'Helper working:', done: 'Helper finished:' },
      write_file: { running: 'Writing:', done: 'Wrote:' },
      append_file: { running: 'Adding to:', done: 'Added to:' },
      edit: { running: 'Editing:', done: 'Edited:' },
      read_file: { running: 'Reading:', done: 'Read:' },
      ls: { running: 'Looking at:', done: 'Looked at:' },
      glob: { running: 'Finding files:', done: 'Found files:' },
      grep: { running: 'Searching for:', done: 'Searched for:' },
      shell: { running: 'Running:', done: 'Ran:' },
      ...m365StepToolsEn,
    },
    unreadable: { one: 'Could not read: {{host}} · {{reason}}', many: 'Could not read: {{count}} sites {{reason}}' },
    reasons: {
      closed: 'closed to automatic reading',
      down: 'site down',
      timeout: 'no answer',
      notFound: 'page not found',
      serverError: 'site error',
      invalid: 'invalid address',
      blocked: 'access blocked',
      redirects: 'redirect loop',
      other: 'unreadable',
    },
    time: { seconds: '{{value}} s', minutes: '{{minutes}} min {{seconds}} s', minutesOnly: '{{minutes}} min' },
  },
  markdown: { copy: 'Copy', copied: 'Copied' },
  usage: {
    turn: '↑ {{input}} · ↓ {{output}} tokens · {{calls}} model calls',
    period: 'Period',
    days_one: 'Last {{count}} day',
    days_other: 'Last {{count}} days',
    seconds_one: '{{count}} s',
    seconds_other: '{{count}} s',
    minutes_one: '{{count}} min',
    minutes_other: '{{count}} min',
    hours: '{{hours}} h {{minutes}} min',
    empty: 'No usage in this period.',
    byMember: 'Usage by member',
    byWorkspace: 'Usage by workspace',
    columns: {
      members: 'Member',
      workspaces: 'Workspace',
      input: 'Input tokens',
      output: 'Output tokens',
      calls: 'Model calls',
      runs: 'Runs',
      sandbox: 'Code run time',
      storage: 'Storage',
      cost: 'Cost',
    },
    unlimited: '{{used}} used · no limit',
    percent: '{{percent}}% used',
    warning: 'Close to the limit',
    full: 'Full',
    budgets: {
      domain: 'Domain',
      allDomains: 'All domains',
      domainHint: 'Admins set the company’s budgets. You set the daily budgets of the members of your domains, up to the company’s daily budget.',
      title: 'Token budgets',
      hint: 'When a budget is used up, no new message can be sent; the person is told when it resets and an entry is logged here. A warning shows from 80%.',
      monthly: "The company's use this month",
      monthlyLimit: 'Monthly company budget (tokens)',
      emptyUnlimited: 'Leave empty for no limit.',
      daily: 'Daily budget per member (prompt tokens)',
      dailyHint: "When empty the platform's value applies: business user {{business}}, developer {{developer}}.",
      today: 'Today',
      own: 'Personal daily budget',
      memberOwn: 'Daily budget for {{name}}',
      inherit: "Empty: the company's value ({{value}})",
      noLimit: 'No limit',
      platformValue: "The platform's value",
      fullToday_one: "Today {{count}} member's budget ran out.",
      fullToday_other: "Today {{count}} members' budgets ran out.",
      resetsDaily: 'Daily budgets reset every night at {{time}}.',
    },
  },
  // the context dialog (BaglamNormal, BaglamDolu; D-118)
  context: {
    label: 'Context: {{percent}} percent full',
    title: 'Context',
    used: '{{used}} / {{window}}',
    estimated: 'Estimated; exact after the first answer.',
    barLabel: 'Context: {{percent}} full',
    status: {
      ok: 'Plenty of room · summarised automatically at {{auto}}',
      warn: '<b>Filling up</b> · summarised automatically in {{left}}',
      high: '<b>Full</b> · summarised automatically with the next message',
    },
    about: {
      system: 'Oppy’s core instructions',
      systemWith: 'Oppy’s core instructions; also {{list}}',
      tools: 'How tools such as web search and file reading are described',
      skills: 'Names and descriptions of the skills that are on',
      skillsLoaded: 'Names and descriptions of the skills that are on; loaded: {{list}}',
      environment: 'Date, language and workspace',
      messages: 'The messages, documents and tool results of this conversation',
    },
    extras: { project: 'the project’s instructions', instructions: 'your own instructions', memory: 'memory' },
    session_one: 'In this chat: {{input}} in · {{output}} out · {{calls}} call',
    session_other: 'In this chat: {{input}} in · {{output}} out · {{calls}} calls',
    compactHint: 'Older messages become a short summary; the conversation goes on.',
    compact: 'Summarise now',
    newConversation: 'New chat',
    categories: {
      system: 'System instructions',
      tools: 'Tools',
      skills: 'Skills',
      environment: 'Environment',
      messages: 'Messages',
    },
  },

  model: {
    defaultOption: 'Default model',
    defaultShort: 'Default',
    isDefaultLower: 'default model',
    thinkingShort: 'Thinks first on hard questions',
    label: 'Model of this conversation',
    hint: 'Changes this conversation only; your default model stays the same.',
    default: 'Default ({{name}})',
    notAllowed: 'not allowed in your company',
    thinking: 'Thinking',
    thinkingHint: 'When off, the model does not think at length before answering; answers come faster.',
    makeDefault: 'Make it my default',
    isDefault: 'Now your default',
  },
  rewind: {
    title: 'Rewind',
    hint: 'The message you pick and everything after it leave the conversation; the message returns to the box for you to edit.',
    none: 'There is no message to rewind to.',
    attachmentsOnly: '(attachment only)',
    removes: '{{count}} messages will leave the conversation.',
    what: 'What goes back?',
    conversationOnly: 'The conversation only',
    withFiles: 'The conversation and the files',
    noFileChanges: 'No file changed after this point.',
    status: { added: 'Will be deleted', modified: 'Goes back', deleted: 'Comes back', type_changed: 'Goes back' },
    confirm: 'Rewind',
  },
  message: {
    edit: 'Edit',
    rewind: 'Rewind to here',
    editHint: 'When you send it, this message and everything after it are replaced.',
    editSend: 'Edit and send',
    good: 'Good answer',
    bad: 'Bad answer',
    badNote: 'What was wrong? (optional)',
    sendFeedback: 'Send',
    regenerate: 'Regenerate',
    regenerateModel: 'Model for the new answer',
    sameModel: 'Same model',
    regenerateWith: 'Regenerate with',
  },
  compaction: {
    auto: 'The earlier part was summarised because the conversation got long',
    manual: 'The conversation was summarised',
    tokens: '{{before}} → {{after}} tokens',
  },
  memoryLine: {
    saved: 'Saved to memory: “{{content}}”',
    removed: 'Removed from memory.',
    open: 'Open memory',
    undo: 'Undo',
  },
  branch: { action: 'Branch from here', from: 'This conversation was branched from:', fork: 'Try in the background (fork)…' },
  diff: { title: 'Change', more: '{{count}} more lines', stats: '{{added}} lines added, {{removed}} lines removed', show: 'Show the change' },
  side: {
    title: 'Side questions',
    hint: 'Asked without changing the conversation; answers are not saved.',
    thinking: 'Answering…',
    button: 'Side question',
  },
  slash: {
    title: 'Commands',
    commands: {
      compress: 'Summarise the conversation to make room',
      clear: 'Start a new conversation',
      rewind: 'Rewind to an earlier message',
      export: 'Download as Markdown or JSON (/export json)',
      btw: 'Ask a side question without changing the conversation',
      model: 'Pick the model of this conversation',
      memory: 'Open the memory page',
      help: 'Show the commands',
      title: 'Rename the conversation',
      branch: 'Branch a new conversation from here',
      fork: 'A copy of the conversation works on something in the background; the result comes back here (/fork directive)',
      recap: 'Show a short recap',
      context: 'Show the context usage',
      skill: 'Add a skill: /skill add <GitHub address, link or library:name>',
    },
    skill: 'skill',
    onlyInConversation: '{{command}} works inside a conversation. Start the conversation first.',
  },
  recap: {
    title: 'Recap',
    pending: 'Preparing the recap…',
    offer: 'You are back after a while. Would you like a short recap?',
    action: 'Recap',
  },
  export: { markdown: 'Download as Markdown', json: 'Download as JSON' },
  memory: {
    title: 'Memory',
    intro: 'Oppy keeps only what you asked it to remember. Your documents and conversations are never written here on their own.',
    disabledByAdmin: 'Memory is turned off in your company. Your company admin can turn it on.',
    enabled: 'Memory on',
    enabledHint: 'When off, nothing stored goes into answers and nothing new is saved.',
    add: 'Add something to remember',
    addPlaceholder: 'E.g. My reports should always be in English, as tables.',
    addButton: 'Add',
    stored: 'Stored ({{count}})',
    empty: 'Nothing stored yet.',
    edit: 'Edit',
    fromConversation: 'You asked in a conversation',
    byYou: 'You added it here',
    workspaceOnly: 'in one workspace only',
    openConversation: 'open the conversation',
  },
  // the library pages' one vocabulary for where a thing lives (D-118; SkillKutuphanesi, BaglantilarYeni, BelgelerYeni)
  library: {
    scopes: { all: 'All', team: 'Team', organization: 'Everyone', user: 'Personal', workspace: 'Workspaces', builtin: 'Standard', conversation: 'Chat attachments' },
    tags: {
      team: 'Team',
      teamNamed: 'Team: {{name}}',
      organization: 'Everyone',
      user: 'Personal',
      workspace: 'Workspace',
      workspaceNamed: 'Workspace: {{name}}',
      builtin: 'Standard',
      builtinFrom: 'Comes with Oppy',
      conversation: 'Chat attachment',
    },
    modes: { default_on: 'On by default', required: 'Required' },
    appliesIn: 'Switches apply in:',
    everywhere: 'Everywhere',
    filter: 'Filter by where it lives',
  },
  skills: {
    deleteTitle: 'Delete the {{name}} skill?',
    deleteConfirm: 'Delete the skill',
    deleteShared: 'It goes for everyone who uses it at the {{level}} level.',
    scope: {
      label: 'Who can use it?',
      short: 'Scope:',
      user: 'Personal',
      userHint: 'Only you',
      workspace: 'A workspace',
      workspaceHint: "The workspace's members",
      team: 'A team',
      teamHint: "The team's members",
      organization: 'Everyone',
      organizationHint: "Everyone in the company · with an admin's approval",
      organizationAdminHint: 'Everyone in the company',
    },
    license: {
      open: '{{name}} · open source',
      closed: 'Not open source; check before sharing it in your company',
      unknown: 'No license information',
    },
    preview: {
      label: 'Preview',
      files: 'Show the files',
      scripts_one: '{{count}} script',
      scripts_other: '{{count}} scripts',
      free: 'Runs without asking',
      nothingFree: 'No command; all are asked',
      asked: 'Asked every time',
      askedAll: 'Writing files and every other command',
      notGiven: 'Not granted',
      license: 'License',
      // Phase 16c.3 (SkillGitHubBuyukDepo): what a skill that asks for nothing runs, the scripts asked by their language
      nothingAsked: 'Nothing; the skill asks for no permission',
      askedScripts: '{{scripts}}, writing files and every other command',
      scriptLanguage_one: '{{count}} {{language}} script',
      scriptLanguage_other: '{{count}} {{language}} scripts',
      languages: { python: 'Python', shell: 'shell', javascript: 'JavaScript', typescript: 'TypeScript', powershell: 'PowerShell', ruby: 'Ruby' },
      more: 'See all',
    },
    // Phase 16c.3: a GitHub repository (SkillGitHubBuyukDepo, SkillGitHubCoklu)
    github: {
      foundOne: '<b>We found 1 skill in this repository: {{name}}</b> ({{files}} files, {{size}}).',
      whole: 'The whole repository is {{files}} files and {{size}}. We download only the skill’s folder; the rest is not installed.',
      wholeCut: 'The repository is very big; GitHub gave part of its file list. We download only the skill’s folder; the rest is not installed.',
      found: '<b>We found {{count}} skills in this repository.</b> Choose the ones to install.',
      onlyChosen: 'Only the folders you choose are downloaded.',
      oneOver: 'One is over the limit; it cannot be installed, the others can.',
      someOver: '{{count}} are over the limit; they cannot be installed, the others can.',
      allOver: 'None is within the limits; you can upload the skill as a ZIP.',
      which: 'Which ones should we install?',
      selected: '{{count}} chosen · each one’s preview opens before installing',
      meta: '{{files}} files · {{size}}',
      metaScripts: '{{files}} files · {{size}} · {{scripts}} scripts',
      over: { bytes: 'Too big: {{size}}', files: 'Too many files: {{files}}', file: 'A file too big: {{size}}' },
      overHint: {
        bytes: 'At most {{limit}} · remove the sample files and upload a ZIP',
        files: 'At most {{limit}} files · remove the sample files and upload a ZIP',
        file: 'A file at most {{limit}} · remove that file and upload a ZIP',
      },
      previewChosen_one: 'Preview {{count}} skill',
      previewChosen_other: 'Preview {{count}} skills',
      installAll: 'Install {{count}} skills',
      installedMany: '{{names}} installed.',
      installedManyHint: 'The assistant uses these skills itself where they fit; you can also run them by typing /name.',
    },
    // Phase 16c.3: why an install failed (SkillGitHubHatalar, SkillGitHubCokBuyuk); a ZIP or SKILL.md link has plainer words
    sourceError: {
      details: 'Technical details',
      copy: 'Copy',
      copied: 'Copied',
      upload: 'Upload a file',
      retry: 'Try again',
      wait: 'Try again in {{minutes}} min',
      seconds: '{{value}} s',
      underTenth: 'under {{value}} s',
      steps: { list: 'Listing the repository', download: 'Downloading the skill folder', linkDownload: 'Downloading the file', read: 'Reading the skill', install: 'Installing' },
      labels: { step: 'Step', address: 'Address', answer: 'Answer', connection: 'Connection step', fallback: 'Classic TLS', hosts: 'Addresses needed', time: 'Time', error: 'Error number' },
      // Phase 16c.4: where a connection through egress stopped, and the classic TLS hello (D-397)
      connectStages: {
        proxy_dns: 'Proxy name (DNS)',
        proxy_tcp: 'Connecting to the proxy (TCP)',
        proxy_answer: 'The proxy’s answer',
        tunnel_tls: 'TLS handshake through the tunnel',
        name_check: 'Oppy’s check of the address (DNS)',
        body: 'The answer broke off',
        unknown: 'Unknown',
      },
      fallbacks: { tried: 'Tried; it did not work either', not_tried: 'Not tried (no time left)', not_needed: 'Not needed (stopped before TLS)' },
      answers: {
        tooLarge: 'Folder {{size}} · {{files}} files · limit {{limit}} and {{limitFiles}} files',
        tooLargeFile: '{{path}} {{size}} · limit for one file {{limit}}',
        rateLimited: 'HTTP {{status}} · requests left {{remaining}} · resets {{time}}',
        rateLimitedNoTime: 'HTTP {{status}} · request limit',
        policy: 'The firm’s policy refused it; no request was sent',
        status: 'HTTP {{status}}',
        timeout: 'Not done within {{seconds}} s',
        network: 'No connection ({{cause}})',
        networkProxy: 'No connection (proxy {{status}})',
        noSkill: '{{files}} files listed, no SKILL.md',
        noSkillCut: '{{files}} files listed (GitHub gave part of the list), no SKILL.md',
        none: 'No answer',
      },
      github: {
        tooLarge: {
          title: 'This skill is too big.',
          bytes: 'The skill folder is {{size}}; at most {{limit}} can be installed. You can remove the sample files and upload it as a ZIP.',
          files: 'The skill folder has {{files}} files; at most {{limit}} files can be installed. You can remove the sample files and upload it as a ZIP.',
          file: '{{path}} is {{size}}; one file can be at most {{limit}}. You can remove that file and upload the skill as a ZIP.',
        },
        rateLimited: {
          title: 'GitHub is making us wait.',
          when: 'Oppy reached the number of requests GitHub allows in an hour. You can try again after {{time}}{{from}} (in about {{minutes}} min).',
          passed: 'Oppy had reached the number of requests GitHub allows in an hour. You can try again now.',
          unknown: 'Oppy reached the number of requests GitHub allows in an hour. Try again a little later.',
          zip: 'If you would rather not wait, you can upload the skill as a ZIP.',
        },
        notAllowed: {
          title: 'Connecting to GitHub is not allowed.',
          allow: 'GitHub’s addresses are not on your firm’s list of allowed sites, so Oppy could not reach the repository.',
          deny: 'GitHub’s addresses are on your firm’s list of blocked sites, so Oppy could not reach the repository.',
          admin: 'Your admin can add them to the list. Meanwhile you can upload the skill as a ZIP.',
          adminDeny: 'Your admin can take them off the list. Meanwhile you can upload the skill as a ZIP.',
        },
        notFound: {
          title: 'This repository was not found.',
          body: 'The address may be mistyped or the repository private. Oppy installs only from public repositories.',
          zip: 'For a private repository, zip the skill folder and upload it.',
        },
        timeout: { title: 'GitHub did not answer in time.', body: 'We waited {{seconds}} seconds and the download did not finish. GitHub may be slow. Try again in a few minutes.' },
        network: {
          title: 'GitHub could not be reached.',
          body: 'The Oppy server could not connect to GitHub. There may be a passing problem with the internet connection or with GitHub.',
          admin: 'If it goes on, give your admin the error number from Technical details.',
        },
        silent: {
          title: 'Oppy connected to GitHub but got no answer.',
          body: 'The connection opened but GitHub did not answer the first message; a smaller message was tried too. This is usually a problem on the server’s route to the internet. If it goes on, give your admin the error number.',
        },
        noSkill: {
          title: 'No skill was found at this address.',
          body: 'The repository has no folder with a SKILL.md file. Paste the link to the skill’s folder, for example <code>github.com/owner/repo/tree/main/skills/name</code>.',
        },
        unavailable: { title: 'GitHub gave an unexpected answer.', body: 'Try again in a few minutes or upload the skill as a ZIP.' },
      },
      link: {
        notAllowed: {
          title: 'Connecting to this address is not allowed.',
          allow: '{{host}} is not on your firm’s list of allowed sites, so Oppy could not reach the file.',
          deny: '{{host}} is on your firm’s list of blocked sites, so Oppy could not reach the file.',
        },
        notFound: { title: 'No file was found at this address.', body: 'The address may be mistyped or the file gone.' },
        timeout: { title: 'The source did not answer in time.', body: 'We waited {{seconds}} seconds and the download did not finish. Try again in a few minutes.' },
        network: { title: 'The source could not be reached.', body: 'The Oppy server could not connect to this address. There may be a passing problem with the connection or the site.' },
        noSkill: { title: 'No skill was found at this address.', body: 'The file or ZIP has no SKILL.md. Paste the link to the SKILL.md file or to the skill’s folder.' },
        unavailable: { title: 'The skill source could not be reached.', body: 'The source gave an unexpected answer. Try again in a few minutes or upload the skill as a ZIP.' },
      },
    },
    introShort: 'Teach Oppy the work you repeat. Skills run in both {{chatProduct}} and {{codeProduct}}.',
    levelsLabel: 'Skill levels',
    searchLabel: 'Search skills',
    addOwn: 'Add your own skill',
    addMenu: { url: 'Add from GitHub or a URL', upload: 'Upload a folder or ZIP', conversation: 'Save from a conversation', form: 'Write a new skill with a form' },
    fromConversation: {
      search: 'Search conversations',
      which: 'Which conversation?',
      messages_one: '{{count}} message',
      messages_other: '{{count}} messages',
      taken: 'What this conversation gives',
      reading: 'Reading the conversation and taking out its steps…',
      noSteps: 'No numbered steps were found; you can finish the draft in the editor.',
      skills: 'Skills used: {{names}}',
      noSkills: 'This conversation used no skills.',
      privacy: 'File contents, figures and personal data never go into the skill; only the steps of the work.',
      footer: 'The draft opens for you to edit before it is saved.',
      create: 'Create the draft',
      title: 'Save from a conversation',
      hint: 'Pick a conversation; a skill draft is made from what you did there, and you can edit it before saving.',
      pick: 'Pick',
    },
    title: 'Skills',
    intro: 'Skills describe work you do often: the steps and, if needed, scripts and templates. The assistant loads a skill on its own when it fits; you can also run one by starting your message with /skill-name. Several skills can be used together in one task.',
    new: 'New skill',
    place: 'Where',
    placeHint: 'With a workspace selected, switching a skill on or off applies there only.',
    everywhere: 'Everywhere (my setting)',
    search: 'Search',
    allLevels: 'All',
    summaryNow: 'Now',
    summaryOn_one: '{{count}} skill on',
    summaryOn_other: '{{count}} skills on',
    summaryTipLabel: 'How much room do they take?',
    summaryTip: 'The names and short descriptions of the skills that are on go to the model with every request; about {{tokens}} tokens now.',
    summarySlash: 'A skill that is off can still be called in a chat by typing “/” and its name.',
    sections: {
      teamTitle: 'Team · {{team}}',
      team: '{{team}} team’s skills',
      teamAuto: '{{team}} team’s skills; on for you when you join the team.',
      organization: 'Added for everyone in your company',
      user: 'Your own skills',
      workspace: 'Apply in the selected workspace',
      builtin: 'Come with Oppy; the same in every company',
      otherTeams: 'Other teams (for management)',
      otherTeamsHint: 'Teams you are not in',
      otherTeamsNote: 'You are not in these teams: you see their skills to manage them, and they stay off for you; you can turn one on for yourself.',
      count_one: '{{count}} skill',
      count_other: '{{count}} skills',
    },
    analyticsLink: 'Skill usage: Management → Skill analytics',
    standard: {
      'web-research': 'Researches the web, reads the sources and summarises with citations.',
      'data-report': 'Builds a report with charts from Excel or CSV data.',
      'skill-writer': 'Helps you write a new skill from a conversation.',
      'code-review': 'Reviews code and suggests fixes and improvements.',
    },
    empty: 'No skills here.',
    details: 'Details',
    on: 'On',
    off: 'Off',
    toggle: 'Turn {{name}} on or off',
    version: 'version {{version}}',
    mayRun: 'May run commands',
    modelOnly: 'Used by the assistant only',
    shadowed: 'A skill of the same name ({{level}}) is used instead of this one.',
    hides: 'Used instead of these skills of the same name: {{levels}}.',
    cost: '~{{listing}} tokens in the list; ~{{body}} tokens when loaded.',
    whenToUse: 'When: {{text}}',
    slashHint: 'To run it: /{{name}} {{hint}}',
    source: 'Source: {{url}}',
    raw: 'Show the SKILL.md file',
    versions: 'Versions',
    inUse: 'In use',
    rollback: 'Use this version',
    submit: 'Send for review',
    approve: 'Approve',
    reject: 'Reject',
    reviewNote: 'review note: {{note}}',
    reviewNotePlaceholder: 'Review note (optional)',
    edit: 'Edit',
    propose: 'Propose a change',
    updateFromSource: 'Update from source',
    download: 'Download (ZIP)',
    confirmDelete: '{{name}} and all its versions are deleted. This cannot be undone.',
    hooksIgnored: 'The hooks of this skill ({{hooks}}) are not run.',
    usageOwn: 'Last 30 days: {{loads}} times · {{users}} people',
    levels: { builtin: 'Standard', organization: 'Everyone', team: 'Team', user: 'Personal', workspace: 'Workspaces' },
    status: { draft: 'Draft', in_review: 'In review', published: 'Published', rejected: 'Rejected' },
    catalog: {
      label: 'For members',
      hint: 'How company and team skills are offered to members',
      available: 'Listed, members turn it on',
      default_on: 'On for everyone, members may turn it off',
      required: 'On for everyone, cannot be turned off',
      requiredHint: 'Your company requires this skill.',
    },
    permissions: {
      title: 'What it may run',
      granted: 'While it runs, these run without asking:',
      ownScripts: "the skill's own scripts",
      scope: 'These hold only in the turn that loaded the skill; turning the skill off or changing it removes them at once. Other commands are always asked.',
      none: 'This skill runs nothing without asking; you are asked before any command.',
      ignored: 'Asked for but not granted:',
    },
    ignoredReasons: {
      tool_level_shell: "all shell commands are never granted; only the skill's own scripts",
      tool_level_write: 'writing files is always asked',
      too_broad: 'too broad (a whole program or any code)',
      unknown_tool: 'unknown tool',
      unsupported_syntax: 'pattern not understood',
    },
    fileKinds: { skill: 'instructions', script: 'script', template: 'template', reference: 'reference', other: 'other' },
    warnings: {
      name_normalized: 'The name was changed to fit the rules.',
      description_long: 'The description is very long; a shorter one is chosen more reliably.',
      body_large: 'The instructions are long; they load in parts.',
      hooks_ignored: 'Hooks are not run.',
      trust_ignored: 'The trust setting was ignored; only a company admin decides it.',
      no_scripts: 'The permissions ask to run scripts, but the package has none.',
      frontmatter_lenient: 'The frontmatter is not strict YAML; it was read line by line. Quote values that contain a colon.',
    },
    add: {
      linkLabel: 'A GitHub repository, a ZIP or a SKILL.md address',
      // 16c.6 (D-433): while the server tries the source once more
      retrying: { github: 'GitHub did not answer; trying again…', link: '{{host}} did not answer; trying again…' },
      examples: 'Examples:',
      sources: 'Source',
      subtitle: 'Before installing we show what it contains and what it may run without asking.',
      subtitleConversation: 'We turn what you did in a conversation into a reusable skill.',
      tabs: { link: 'From a link', file: 'From a file', write: 'Write your own', conversation: 'From a conversation' },
      dropTitle: 'Drop a ZIP, a folder or a SKILL.md here',
      pickFile: 'Choose a file',
      pickFolder: 'Choose a folder',
      dropHint: '.zip or .md · up to 20 MB · several skills inside are all listed',
      read: 'Read',
      removeFile: 'Remove the file',
      dragHint: 'You can drop a ZIP or a folder on this window',
      writeTitle: 'Write your own skill',
      writeHint: 'You write its name, what it does and its steps in a form or straight as SKILL.md, and try it before saving.',
      writeStart: 'Start writing',
      open: 'Open the skill',
      button: 'Add a skill',
      title: 'Add a skill',
      hint: "A GitHub repository (owner/repo or a link), a link to a ZIP or SKILL.md, or library:name from your company's library. You see what it contains and what it may run before you install it.",
      source: 'Source',
      placeholder: 'e.g. anthropics/skills/skills/xlsx or library:monthly-report',
      preview: 'Preview',
      orUpload: 'or upload:',
      uploadFile: 'ZIP or SKILL.md',
      uploadFolder: 'Folder',
      commit: 'Version (commit): {{commit}}',
      chooseFolder: 'This source holds several skills',
      chooseFolderHint: 'Choose a folder…',
      files: '{{count}} files',
      existing: 'You have a skill of this name ({{level}}, version {{version}}). Installed in the same place, it becomes a new version.',
      install: 'Install',
      update: 'Install as a new version',
      installed: '{{name}} is installed.',
      installedHint: 'The assistant uses {{name}} on its own when it fits; you can also run it with /{{name}}.',
    },
    target: {
      label: 'Install to',
      propose: 'Propose for everyone',
      workspace: 'Workspace',
      team: 'Team',
    },
    editor: {
      mode: 'Editing as',
      side: 'Preview and try',
      argumentHintHint: 'Shown next to the name in the "/" menu.',
      whenToUseHint: 'Write example requests; the assistant looks at them too when it chooses.',
      nameHintNamed: 'Lower case and hyphens. It also runs as /{{name}} in a conversation.',
      nameOk: 'Fine',
      namePlaceholder: 'skill-name',
      descriptionPlaceholder: 'What it does shows here',
      addFiles: 'Add files',
      addFilesHint: 'A script, a template or a reference file. When you add a script, you choose the command that may run without asking.',
      grantScripts: 'Let the added scripts run without asking',
      filesKept: "This skill's {{count}} files stay as they are.",
      menuPreview: 'How it looks in the "/" menu',
      checksTitle: 'Checks',
      checks: {
        frontmatter: 'SKILL.md must start with its header between --- lines',
        noName: 'Write a name',
        badName: 'Use lower-case letters, digits and hyphens',
        taken: 'A skill with this name exists ({{level}}); saving adds a second one',
        name: 'The name is fine and unique',
        noDescription: 'Say what it does',
        shortDescription: 'Say concretely what it produces',
        longDescription: 'Keep the description under {{max}} characters',
        description: 'The description says concretely what it produces',
        noWhen: 'Example requests under "When to use" help it to be chosen',
        when: 'When to use it is written',
        noBody: 'Write the instructions',
        noSteps: 'Numbered steps make the instructions easier to follow',
        steps: 'Step-by-step instructions',
        longBody: 'Long instructions load in parts',
      },
      try: 'Try without saving',
      tryLabel: 'A request to try',
      tryPlaceholder: 'e.g. Tell Ahmet the meeting moved to Friday',
      tryRun: 'Try',
      tryNote: 'Nothing is saved; the test turn counts toward your usage budget.',
      tryChosen: 'The skill was chosen by itself · ready in {{seconds}} s',
      tryNotChosen: 'The skill was not chosen by itself; tried as if run with /{{name}} · {{seconds}} s',
      tryOther: 'The assistant first chose the {{other}} skill; the draft was tried as if run by its name · {{seconds}} s',
      newTitle: 'New skill',
      editTitle: 'Edit {{name}}',
      fromConversation: 'This draft was made from the conversation. Check it, then save it.',
      form: 'Form',
      name: 'Name',
      nameHint: 'Lower case and hyphens, e.g. monthly-revenue-report. It also runs as /name.',
      description: 'What it does',
      descriptionHint: 'The assistant chooses the skill by this sentence. Say concretely what it produces.',
      whenToUse: 'When to use it',
      argumentHint: 'Argument hint',
      body: 'Instructions',
      bodyHint: 'About {{tokens}} tokens. Put long details in separate files.',
      allowedTools: 'Commands that may run without asking (optional)',
      allowedToolsHint: "One command pattern per line. Only specific commands and the skill's own scripts are granted.",
      rawHint: 'Frontmatter (---) and instructions. The form and this field edit the same skill.',
      changeNote: 'Change note',
      saveVersion: 'Save as a new version',
    },
    review: { title: 'Proposals waiting for review ({{count}})' },
  },
  connectors: {
    deleteTitle: 'Delete the {{name}} connection?',
    title: 'Connections',
    intro: 'Connections (MCP) let the assistant use tools of other services: calendars, document stores, databases, internal systems. Your keys and sign-ins are kept encrypted on the server and never sent back to the browser.',
    placeHint: "With a workspace selected, its connections and the switches there are shown.",
    one: 'connection',
    tool: 'connection tool',
    // BaglantilarYeni (D-118)
    introShort: 'Let Oppy use the tools of other systems: email and calendars, document stores, databases, internal systems.',
    introPersonal: 'Lets Oppy use the tools of other services.',
    safe: 'Your keys and sign-ins are kept encrypted on the server and never sent back to the browser.',
    search: 'Search connections',
    summaryNow: 'Now',
    summaryOn_one: '{{count}} connection on',
    summaryOn_other: '{{count}} connections on',
    summaryTools_one: 'the assistant can use {{tools}} tool',
    summaryTools_other: 'the assistant can use {{tools}} tools',
    sections: {
      teamTitle: 'Team · {{team}}',
      team: "Your team's connections",
      organization: 'Added for everyone in the company',
      user: 'Your own connections',
      workspace: 'Added to workspaces',
      count_one: '{{count}} connection',
      count_other: '{{count}} connections',
    },
    emptyAll: 'No connections yet.',
    emptyScope: {
      organization: 'Your company has not added connections for everyone yet.',
      team: 'Your teams have no connections.',
      user: 'You have no connections of your own yet.',
      workspace: 'No workspace has connections.',
    },
    state: { ready: 'Ready', account: 'Connect your account', down: 'Not reachable' },
    location: { local: 'In the code workspace', internal: 'Internal', remote: 'Remote server' },
    moreTools_one: '+{{count}} tool',
    moreTools_other: '+{{count}} tools',
    toolsAfterSignIn: 'Its tools show once you connect your account.',
    connectAccount: 'Connect my account',
    eachOwnAccount: 'Everyone connects their own account',
    details: 'Details',
    actions: 'Actions for {{name}}',
    scopes: { tenant: 'Everyone', user: 'Personal', workspace: 'Workspaces' },
    trusted: 'Trusted by the company',
    required: 'Required',
    blocked: {
      disabled: 'Off',
      plan_required: 'Opens on Pro',
      disabled_by_admin: 'Your company turned personal connections off',
      untrusted_workspace: 'Does not run in an untrusted workspace',
      local_in_chat: 'Local connections run only in code workspaces',
      not_approved: 'The shared configuration waits for your approval',
      needs_auth: 'You need to sign in',
      needs_credentials: 'You need to enter a key',
      not_discovered: 'Its tools have not been read yet',
      error: 'Not reachable right now',
    },
    tools: 'Tools ({{count}})',
    noTools: 'No tool list yet. Read it again with "Refresh".',
    readOnly: 'read-only',
    policyFor: 'Permission for {{tool}}',
    policy: { ask: 'Ask every time', allow: 'Run without asking', deny: 'Off' },
    instructions: "The service's notes",
    lastError: 'The last connection attempt failed.',
    lastUsed: 'Last used: {{date}}',
    toggle: 'Turn {{name}} on or off',
    signIn: 'Sign in',
    signInAgain: 'Sign in again',
    enterKeys: 'Enter key',
    forgetKeys: 'Delete my key',
    approve: 'Approve the configuration',
    refresh: 'Refresh',
    restart: 'Restart',
    trust: 'Mark as trusted',
    untrust: 'Remove trust',
    trustHint: 'On a trusted connection only the tools the service marks read-only run without asking. Tools that change or send something are still asked; to run one without asking, set its permission to "Run without asking".',
    confirmDelete: 'Its sign-ins and stored keys are deleted too.',
    userServersOff: 'Your company turned adding your own connections off; you can use those in the Everyone section.',
    credentials: { shared: 'These values are used for everyone in the company.', personal: 'These values are kept for your connection only.' },
    oauth: { connected: 'Connected.', failed: 'Sign-in did not work.' },
    add: {
      button: 'Add a connection',
      title: 'Add a connection',
      hint: 'See its tools with "Test the connection" first, then save it.',
      where: 'For',
      workspace: 'Workspace',
      kind: 'Kind',
      http: 'Remote server (HTTP)',
      sse: 'Remote server (SSE, older)',
      stdio: "Local command (in a code workspace's sandbox)",
      name: 'Short name',
      nameHint: 'Lower case letters, digits and hyphens; it shows in tool names.',
      displayName: 'Display name',
      description: 'Description',
      stdioHint: "A local connection runs only in your code workspace's sandbox, with your rights.",
      command: 'Command',
      args: 'Arguments',
      argsHint: 'One argument per line.',
      url: 'Address',
      auth: 'Authentication',
      authNone: 'None',
      authOauth: 'Sign in (OAuth)',
      authHeaders: 'API key or header',
      oauthScope: 'Scope (optional)',
      oauthHint: 'Most services work with it empty.',
      oauthAfterSave: 'After saving, connect with "Sign in".',
      env: 'Environment variables',
      headers: 'Headers',
      pairName: 'Name',
      pairValue: 'Value',
      addPair: 'Add',
      secretsHint: 'Values are kept encrypted on the server and never shown again.',
      catalog: 'For members',
      shared: 'Everyone uses this key',
      import: "Import another app's JSON settings",
      importButton: 'Import',
      test: 'Test the connection',
      testOk: 'The connection works: {{count}} tools found.',
      testFailed: 'Could not connect.',
      webPage: 'This address is not a connection (MCP) server; it answered with a web page. Connection addresses usually end in /mcp.',
      webPageDetails: 'HTTP {{status}} · {{type}}',
      githubRepo: 'This looks like a GitHub repository. To install a skill, use Add a skill.',
      toSkill: 'Go to Add a skill',
    },
  },
  teams: {
    deleteTitle: 'Delete the {{name}} team?',
    title: 'Teams',
    intro: "Teams share their own skills in the skill library. A team's lead manages the team's skills.",
    empty: 'No teams yet.',
    count: '{{count}} members',
    lead: 'Lead',
    member: 'Member',
    role: 'Role',
    remove: 'Remove',
    addMember: 'Add to team',
    pickMember: 'Choose a member…',
    new: 'Create team',
    newPlaceholder: 'Team name, e.g. Finance',
    confirmDelete: "The team's skills are deleted too.",
  },
  webPolicy: {
    title: 'Web and integrations',
    intro: "Rules for the assistant's web search and page reading; installing skills from outside sources and personal connections.",
    provider: 'Web search',
    platformMissing: 'Platform search is not set up here; choose another provider.',
    providers: { platform: "The platform's search", searxng: 'Our own SearXNG server', off: 'Off' },
    searxngUrl: 'SearXNG address',
    apiKey: 'API key',
    keySaved: 'A key is saved (not shown).',
    keyMissing: 'No key entered.',
    saveKey: 'Save key',
    removeKey: 'Delete key',
    fetch: 'Reading web pages',
    fetchHint: 'The assistant may read pages the user gave or a search returned.',
    allow: 'Only these sites',
    allowHint: 'Empty means every site; otherwise only these (one domain per line).',
    deny: 'Blocked sites',
    denyHint: 'One domain per line.',
    skillSources: 'Installing skills from outside sources',
    skillSourcesHint: 'When off, skills come only from the Everyone section.',
    userServers: 'Personal connections',
    userServersHint: 'When off, members use only the connections in the Everyone section.',
  },
  skillChip: {
    skill: 'Skill',
    loaded: '{{name}} loaded',
    runByYou: '/{{name}} ran',
    reused: '{{name}} was already loaded; nothing was added',
    part: 'part {{part}}/{{parts}}',
    grants: 'this turn without asking: {{list}}',
    grantedBy: 'allowed by the {{name}} skill',
    added: 'Added: {{list}}',
    removed: 'Removed or turned off: {{list}}',
  },
  sources: { title: 'Sources ({{count}})' },
  documents: {
    deleteTitle: 'Delete {{name}}?',
    title: 'Documents',
    intro: 'Your documents live here. Conversations search them; answers cite the page, row or slide.',
    button: 'Documents ({{count}})',
    inConversation: "This conversation's documents",
    noneInConversation: 'No documents in this conversation yet. Attach a file or pick one from the library.',
    removeFromConversation: 'Remove {{name}} from this conversation',
    addFromLibrary: 'Add a document from the library',
    libraryEmpty: 'No library documents to add',
    openLibrary: 'Open the document library',
    upload: 'Upload documents',
    uploadTo: 'Where',
    accepted: 'PDF, Word, Excel, PowerPoint, text, Markdown, CSV and pictures.',
    uploaded_one: '{{count}} document uploaded; it is being read.',
    uploaded_other: '{{count}} documents uploaded; they are being read.',
    filter: 'Filter documents',
    all: 'All',
    search: 'Search by name',
    noMatch: 'No document with that name.',
    empty: 'No documents yet. Documents you upload show here.',
    share: 'Share',
    shareWith: 'Share with…',
    keep: 'Keep in the library',
    workspaceNamed: 'Workspace: {{name}}',
    actions: 'Actions for {{name}}',
    confirmDelete: 'Its text, index and previews are deleted too; sources pointing to it will no longer open.',
    scope: { user: 'Personal', workspace: 'Workspaces', organization: 'Everyone', conversation: 'Chat attachments' },
    // BelgelerYeni (D-118)
    uploadTarget: 'Where should it go?',
    uploadPlace: 'goes to',
    dropHere: 'Drop files here',
    dropOr: 'or',
    choose: 'choose from your computer',
    acceptedShort: 'PDF, Word, Excel, PowerPoint, text, Markdown, CSV, pictures · up to {{mb}} MB',
    columns: { name: 'Document', where: 'Where', source: 'Source', size: 'Size', chat: 'In a chat', state: 'State', added: 'Added' },
    sort: { label: 'Order', option: 'Order: {{order}}', date: 'Newest first', dateAsc: 'Oldest first', name: 'By name', size: 'By size' },
    source: {
      filter: 'Source',
      all: 'All',
      upload: 'From computer',
      onedrive: 'OneDrive',
      sharepoint: 'SharePoint',
      sharepointSite: 'SharePoint · {{site}}',
      outlook: 'Outlook attachment',
      outlookFrom: 'Outlook · {{sender}}',
      box: 'Source',
      fromMicrosoft: 'From Microsoft 365…',
    },
    sourceState: {
      importing: 'Importing',
      refetching: 'Fetching again…',
      importFailed: 'Could not be fetched',
      refetchFailed: 'Could not be fetched again',
      changed: 'Changed at the source',
      gone: 'Not found at the source',
      unchanged: 'No change at the source',
      updated: 'Fetched again',
    },
    aside: {
      fetchedBy: 'Fetched by',
      atSource: 'At the source',
      where: 'Where',
      size: 'Size',
      fetched: '{{name}} · {{when}}',
      changedBy: '{{by}} changed it {{when}}',
      changed: 'Changed {{when}}',
      place: {
        user: 'Personal · only you see it',
        team: 'Team: {{name}} · everyone in the team sees it',
        workspace: 'Workspace: {{name}} · everyone in the workspace sees it',
        organization: 'Everyone · everyone in the company sees it',
        conversation: 'Chat attachment · used in that chat only',
      },
      whole: 'read in full',
      large: 'large · answered from the relevant parts',
      copy: {
        sharepoint: 'This is a copy. If the file changes in SharePoint, it does not change here by itself; update it with “Fetch again”.',
        onedrive: 'This is a copy. If the file changes in OneDrive, it does not change here by itself; update it with “Fetch again”.',
        outlook: 'This is a copy. Mail attachments do not change.',
      },
      refetchFailed: 'Could not be fetched again: {{reason}}. The old copy stays.',
      unchanged: 'No change at the source; the copy stayed the same.',
      updated: 'Fetched again; the copy is up to date.',
    },
    refetch: {
      action: 'Fetch again',
      busy: 'Fetching again…',
      connect: 'Connect your Microsoft account to fetch it again',
      scopes: 'New permission needed',
      reconnect: 'reconnect',
      off: 'Fetching documents from Microsoft 365 is turned off right now; the copy stays in Documents.',
      switchedOff: 'You turned Microsoft 365 off under Connections; turn it on to fetch again.',
      gone: 'The file was not found in Microsoft; it may have been deleted or moved.',
      forbidden: 'You cannot see this file in Microsoft, so you cannot fetch it again.',
      outlook: 'Mail attachments do not change',
    },
    when: { today: 'today at {{time}}', yesterday: 'yesterday at {{time}}', date: 'on {{date}}' },
    sections: {
      teamTitle: 'Team · {{team}}',
      organization: 'added for everyone',
      user: 'only yours',
      workspace: 'in workspaces',
      conversation: 'files added to chats',
      count_one: '{{count}} document',
      count_other: '{{count}} documents',
    },
    inChat: 'in the chat “{{title}}”',
    inSomeChat: 'Attached to a chat',
    chat: {
      whole: 'Read in full',
      large: 'Large · answered from the relevant parts',
      wholeTokens: '~{{tokens}} tokens',
      largeTokens: '~{{tokens}} tokens · ~{{question}} per question',
      pending: 'Known once it is ready',
    },
    why: 'Why?',
    retry: 'Try again',
    rename: 'Rename',
    explain: '“Read in full”: the whole document goes to the model with the question. “Large”: only the parts that match the question are found and cited; summarising the whole document takes long.',
    size: {
      pages_one: '{{count}} page',
      pages_other: '{{count}} pages',
      sheets_one: '{{count}} sheet',
      sheets_other: '{{count}} sheets',
      slides_one: '{{count}} slide',
      slides_other: '{{count}} slides',
      tokens: '~{{tokens}} tokens',
    },
    state: {
      queued: 'Waiting',
      ready: 'Ready',
      failed: 'Could not be read: {{reason}}',
      failedShort: 'Could not be read',
      processing: 'Processing',
      processingAt: 'Processing {{percent}}%',
      importFailed: 'Could not be fetched: {{reason}}',
    },
    stage: { importing: 'Importing', extracting: 'Reading the text', converting: 'Converting', ocr: 'Recognizing text', indexing: 'Indexing' },
    errors: {
      encrypted: 'it is protected with a password',
      invalid_document: 'the file is damaged or unreadable',
      archive_too_large: 'too large when unpacked',
      timeout: 'reading took too long',
      conversion_failed: 'it could not be converted',
      ocr_failed: 'the text could not be recognized',
      unsupported: 'this type cannot be read',
      empty: 'the file is empty',
      processing_failed: 'an unexpected problem occurred',
      source_forbidden: 'you may not see this file in Microsoft',
      source_not_found: 'the file was not found in Microsoft; it may have been deleted or moved',
      source_too_large: 'the file is over the size limit',
      source_unsupported: 'this file type cannot be read',
      source_malware: 'Microsoft found malware in this file',
      source_empty: 'the file is empty',
      source_off: 'fetching documents from Microsoft 365 is turned off',
      source_not_connected: 'no Microsoft account is connected; connect it and try again',
      microsoft_scopes_required: 'a new permission is needed; reconnect your Microsoft account',
      source_unavailable: 'Microsoft could not be reached; try again in a little while',
    },
    estimate: {
      fits: 'About {{tokens}} tokens; fits the context. Each question adds about {{question}} tokens.',
      large: 'About {{tokens}} tokens; larger than the context. Questions are answered from the relevant parts (about {{question}} tokens each); a summary of the whole takes long.',
    },
    quality: { good: 'good', fair: 'fair', low: 'low' },
    notes: {
      ocr_used: 'Text was recognized from images on {{pages}} pages (quality: {{quality}}).',
      ocr_low_quality: 'Text was recognized from images on {{pages}} pages, but the quality is low: numbers and names may be misread.',
      truncated: 'Only the first {{pages}} pages were read (of {{total}}).',
      no_text: 'No readable text was found in the document.',
      image_no_text: 'The picture has no readable text; a model that understands pictures is not available yet.',
      sheet_truncated: 'Only part of the sheet {{sheet}} was read.',
      keyword_only: 'Meaning-based search is not available now; the document is searched by words.',
    },
    preview: {
      title: 'Preview',
      previous: 'Previous page',
      next: 'Next page',
      pageOf: 'Page {{page}} of {{total}}',
      slideOf: 'Slide {{page}} of {{total}}',
      openFull: 'Open the document full screen',
      quote: 'The passage in the source',
      recognized: 'Recognized text',
      empty: 'Nothing to show.',
      truncated: 'Only the first rows are shown.',
      notYet: 'The preview is ready once the document is read.',
    },
  },
  citations: {
    title: 'Sources',
    page: 'p. {{number}}',
    slide: 'slide {{number}}',
    row: 'row {{number}}',
    sheet: 'sheet',
    section: 'part {{number}}',
    gone: "This source's document is gone (deleted, or you no longer have access). The quote:",
    searched: 'Searched {{count}} documents · {{sources}} sources',
    searchedNone: 'Searched {{count}} documents',
  },
  convTools: {
    button: 'Skills and connections',
    skills: 'Skills in this conversation',
    hint: 'Switches here apply to this conversation only.',
    saveAsSkill: 'Save this as a skill',
    manageSkills: 'Manage skills',
    connectors: 'Connections in this conversation',
    noConnectors: 'No connections available.',
    manageConnectors: 'Manage connections',
    addSkill: 'Add a skill',
  },
  admin: {
    title: 'Administration',
    nav: 'Administration menu',
    onlyAdmins: 'Only company admins can open this page.',
    onlySuperAdmins: 'Only super admins can open this page.',
    sections: {
      access: 'Access & permissions',
      models: 'Models',
      integrations: 'Integrations',
      usage: 'Usage & limits',
      policies: 'Approval policies',
      skills: 'Skill analytics',
      audit: 'Audit log',
      identity: 'Authentication',
      license: 'License',
    },
    csv: 'CSV',
    csvExport: 'Export (CSV)',
    table: {
      selectPage: 'Select the rows on this page',
      actions: 'Actions',
      empty: 'No rows to show.',
      range: '{{from}}–{{to}} of {{total}} {{noun}}',
      perPage: 'Per page',
      pages: 'Pages',
      previous: 'Previous page',
      next: 'Next page',
    },
    period: { label: 'Period: {{period}}', d1: 'Today', d7: 'Last 7 days', d30: 'Last 30 days', d90: 'Last 90 days' },
    access: {
      directoryInfo: 'People join at their first sign-in with their company account. Roles and domains come from the company directory; here access is only turned off and on.',
      tabsLabel: 'Access & permissions sections',
      tabs: { members: 'Members', domains: 'Domains', system: 'System accounts', teams: 'Teams' },
      search: 'Search name, e-mail or user name',
      domain: 'Domain',
      role: 'Role',
      all: 'All',
      allDomains: 'All ({{count}})',
      roles: { owner: 'Super admin', admin: 'Admin', domain_admin: 'Team admin', member: 'Member' },
      roleFromDirectory: 'The role comes from the company directory',
      status: { active: 'Active', deactivated: 'Access off', inactive: 'Not signed in for 30 days' },
      columns: { member: 'Member', domains: 'Domain', role: 'Role', lastSignIn: 'Last sign-in', status: 'Status' },
      noun: 'members',
      nounActive: 'active members',
      lastSignIn: { never: 'Never', today: 'Today {{time}}', yesterday: 'Yesterday {{time}}', days_one: '{{count}} day ago', days_other: '{{count}} days ago' },
      menu: {
        label: 'Actions for {{name}}',
        usage: 'See usage and budget',
        audit: 'Show in the audit log',
        sessions: 'End sessions',
        deactivate: 'Turn access off',
        reactivate: 'Turn access on',
        remove: 'Remove from the company',
      },
      confirm: {
        sessionsTitle: 'End the sessions of {{name}}?',
        sessionsBody: 'Their open sessions end at once; they can sign in again.',
        deactivateTitle: 'Turn off the access of {{name}}?',
        deactivateBody: 'Their sign-in is refused and their open sessions end at once. Their Microsoft connection, if any, is removed and its key deleted; nothing else is deleted. You can turn their access on again.',
      },
      done: { sessions_one: '{{count}} session ended.', sessions_other: '{{count}} sessions ended.' },
      domains: {
        search: 'Search domain or workspace',
        columns: { domain: 'Domain', members: 'Members', admins: 'Admins', workspace: 'Workspace', skills: 'Team skills' },
        noun: 'domains',
        none: 'None',
        more: '+{{count}}',
      },
      system: {
        hint: 'The break-glass admin is a system account: it signs in with a password and a two-step code when the company sign-in cannot be used. It is in no member list, count, budget or usage table, and the operator creates it with a command.',
        columns: { account: 'Account', twoFactor: 'Two-step verification', lastUsed: 'Last used', created: 'Created' },
        on: 'On',
        off: 'Off',
        never: 'Never used',
        empty: 'There is no break-glass admin.',
      },
      relink: {
        title: 'Company accounts waiting for approval',
        hint: 'When the company directory is rebuilt, a person comes back with a new identity and no second account is opened. Link it and they sign in to their own account; their conversations, documents and skills stay theirs.',
        detail_one: 'New user name: {{username}} · {{count}} attempt · last: {{when}}',
        detail_other: 'New user name: {{username}} · {{count}} attempts · last: {{when}}',
        link: 'Link the account',
      },
    },
    usage: {
      tabsLabel: 'Usage & limits sections',
      tabs: { summary: 'Summary', members: 'Members', workspaces: 'Workspaces', feedback: 'Answer ratings' },
      cards: {
        month: "The company's use this month",
        monthDomains: 'Your domains’ use this month',
        noMonthLimit: 'tokens · no monthly limit',
        monthLimit: 'tokens · monthly limit {{limit}}',
        today: 'Used today',
        todayUnit: 'members',
        full: 'Budget used up',
        near: 'Near the limit',
        show: 'Show the members',
      },
      defaults: {
        title: 'Default budgets',
        hint: 'They apply unless a domain or a person has a value of its own.',
        monthly: "The company's monthly budget",
        noLimit: 'No limit',
        daily: 'Daily budget per member',
        dailyUnit: 'prompt tokens',
        platform: "The platform's value ({{business}} business user, {{developer}} developer)",
        readOnly: "Company admins change the company's budgets; you can change your domains' budgets.",
      },
      domains: {
        title: 'Domains',
        hint: "Team admins change only their own domains' budgets, within the company's limit.",
        columns: { domain: 'Domain', members: 'Members', today: 'Today', budget: 'Daily budget per person', state: 'Status' },
        full: '{{count}} used up',
        near: '{{count}} near',
        members: 'Members',
        edit: 'Edit budget',
        editLabel: 'Edit budget: {{name}}',
        footnote: 'The break-glass admin is a system account; it is in no list or count.',
      },
      source: { personal: 'Own', domain: 'Domain', organization: 'Company', platform: 'Platform' },
      state: { full: 'Used up', near: 'Near' },
      members: {
        search: 'Search name or e-mail',
        chipsLabel: 'Budget status',
        chips: { all: 'All', full: 'Used up', near: 'Near the limit', custom: 'Own budget', idle: 'Not used today' },
        columns: {
          member: 'Member',
          domain: 'Domain',
          today: 'Today',
          month: 'This month',
          budget: 'Daily budget',
          input: 'Input tokens',
          output: 'Output tokens',
          calls: 'Model calls',
          runs: 'Runs',
          sandbox: 'Code runs',
          storage: 'Storage',
          cost: 'Cost',
        },
        columnsMenu: 'Columns',
        noun: 'members',
        note: 'heaviest users first',
        select: 'Select {{name}}',
        selected_one: '{{count}} member selected',
        selected_other: '{{count}} members selected',
        assign: 'Set a daily budget',
        reset: 'Back to the default',
        clear: 'Clear the selection',
        editLabel: 'Edit the daily budget of {{name}}',
        bulkSet: '{{count}} members got a daily budget of {{value}}.',
        bulkReset: "{{count}} members' budgets went back to the default.",
      },
      editor: {
        title: '{{name}} · daily budget',
        usedToday: 'Used {{used}} today · resets at {{time}}',
        inherited: 'Default · {{value}} ({{source}})',
        from: { domain: "the domain's value", organization: "the company's value", platform: "the platform's value" },
        own: 'Their own',
        bulkTitle_one: 'Daily budget for {{count}} member',
        bulkTitle_other: 'Daily budget for {{count}} members',
        bulkHint: 'Each selected member gets this value as their own budget; every change is recorded in the audit log.',
        domainTitle: '{{name}} · daily budget per person',
        domainInherit: "The company's value · {{value}}",
        domainPlatform: "The platform's value · {{business}} / {{developer}}",
        domainOwn: "The domain's own",
        tooBig: 'This is above the company’s limit; a company admin can change it.',
      },
      workspaces: {
        search: 'Search workspace',
        kind: 'Type',
        kinds: { chat: '{{chatProduct}}', code: '{{codeProduct}}' },
        columns: { workspace: 'Workspace' },
        total: 'Total ({{count}} workspaces)',
        noun: 'workspaces',
        costNote: 'Costs use the token prices on the Models page. A "run" is one assistant turn, from a request to its answer.',
        noPrices: 'No model has a price, so costs show as "—"; enter prices on the Models page. A "run" is one assistant turn, from a request to its answer.',
      },
    },
    skills: {
      cards: {
        loads: 'Skill loads',
        loadsSub: '{{skills}} skills · {{people}} people',
        model: 'Chosen by the model',
        modelSub: '{{percent}} called by name (/skill)',
        failed: 'Failed loads',
        failedSub: '{{percent}} · details in the audit log',
        unused: 'Never used',
        unusedSub: 'Everyone and Team skills',
      },
      search: 'Search skill',
      scope: 'Scope',
      scopes: { builtin: 'Standard', organization: 'Everyone', team: 'Team', personal: 'Personal' },
      domainChip: 'Team: {{name}}',
      personalRow: 'Personal skills (together)',
      columns: { skill: 'Skill', loads: 'Loads', how: 'How it was loaded', failed: 'Failed', users: 'People', tokens: 'Tokens (skill)', lastUsed: 'Last used' },
      byModel: '{{percent}} by the model',
      noun: 'skills',
      note: 'most loaded first',
      privacy: 'No conversation content.',
      unusedWarning_one: '{{count}} skill was not used in {{days}} days:',
      unusedWarning_other: '{{count}} skills were not used in {{days}} days:',
      openList: 'Open the list',
      closeList: 'Close the list',
      lastUsed: { today: 'Today', yesterday: 'Yesterday', days_one: '{{count}} day ago', days_other: '{{count}} days ago' },
    },
    audit: {
      hint: 'Sign-in, access, budget, model, connection, license and system events in one place. Conversation content and what is sent to connections never appear here.',
      search: 'Search people, events or details',
      person: 'Person',
      everyone: 'Everyone',
      chosen: 'Chosen person',
      personSearch: 'Search person',
      date: 'Date',
      dates: { d1: 'Today', d7: 'Last 7 days', d30: 'Last 30 days', d90: 'Last 90 days', d0: 'All' },
      warnings: 'Warnings only',
      csv: 'CSV (last 90 days)',
      columns: { time: 'Time', event: 'Event', who: 'Who', kind: 'Type', details: 'Details' },
      today: 'Today · {{date}}',
      yesterday: 'Yesterday · {{date}}',
      noun: 'events',
      note: 'newest first',
      tabsLabel: 'Event kinds',
      tabs: { all: 'All', access: 'Sign-in and access', budgets: 'Budgets', models: 'Models', connections: 'Connections', license: 'License', system: 'System' },
      kinds: { access: 'Sign-in and access', budgets: 'Budgets', models: 'Models', license: 'License', system: 'System', skills: 'Skills', integrations: 'Connections' },
      titles: {
        firstLogin: 'First sign-in · account created',
        dailyBudget: 'Daily budget changed',
        monthlyBudget: 'Monthly budget changed',
        internalOn: 'Internal network addresses allowed',
        internalOff: 'Internal network addresses turned off',
        // Phase 16c: a backup by how it started; an installation's first start
        nightlyBackup: 'Nightly backup taken',
        nightlyBackupFailed: 'Nightly backup failed',
        installed: 'Oppy installed',
      },
      method: { directory: 'With the company account', password: 'With e-mail and password', break_glass: 'Emergency sign-in', local: 'With e-mail and password' },
      remember: 'remember me: {{value}}',
      linked: 'joined the local account',
      reason: {
        invalid: 'Wrong password',
        locked: 'Tried before the wait was over',
        unavailable: 'The sign-in service could not be reached',
        deactivated: 'Access is off',
        totp: 'Wrong verification code',
      },
      // Phase 16c.3: a skill installed from a source, or one that could not be installed or updated, and why
      skill: {
        reason: {
          skill_too_large: 'Skill too big ({{size}})',
          skill_too_large_files: 'Skill too big ({{files}} files)',
          skill_source_rate_limited: 'GitHub request limit',
          rateUntil: 'GitHub request limit · until {{time}}',
          skill_source_not_allowed: 'Not on the allowed sites ({{hosts}})',
          denied: 'On the blocked sites ({{hosts}})',
          skill_source_not_found: 'Not found or private',
          skill_source_timeout: 'Time-out ({{seconds}} s)',
          skill_source_network: 'Network error',
          skill_no_skill_md: 'No SKILL.md',
          skill_source_unavailable: 'Source not reached',
          unavailableStatus: 'The source answered {{status}}',
          skill_invalid: 'Skill file not readable',
          payload_too_large: 'File too big',
          skill_external_disabled: 'Outside sources turned off',
          name_taken: 'A skill with this name exists',
          provider_url_blocked: 'Internal address',
          other: 'Not installed ({{code}})',
        },
      },
      connections: {
        scopes: { tenant: 'Everyone', workspace: 'Workspace', user: 'Personal' },
        configChanged: 'address or settings changed',
        policy: { ask: 'Ask every time', allow: 'Run without asking', deny: 'Off' },
        method: { oauth: 'by signing in', key: 'with a key' },
        shared: 'for everyone',
        auto: 'Not asked: {{reason}}',
        approved: 'Approved (once)',
        approvedAlways: 'Approved (lasting permission)',
        rejected: 'Rejected',
        denied: 'Blocked: {{reason}}',
        reasons: {
          trusted_read: 'trusted connection, read-only',
          tool_policy: 'allowed by the admin',
          rule: 'lasting permission',
          full_access: 'full access',
          plan_mode: 'plan mode',
          rule_deny: 'a rule',
          other: 'other',
        },
        result: { ok: 'succeeded', error: 'error' },
        seconds: '{{seconds}} s',
        helper: 'helper',
        conversation: 'conversation {{id}}',
      },
      attempt: 'attempt {{count}}',
      refreshRefused: 'The company sign-in did not renew the session',
      domainsAdded: 'Added: {{domains}}',
      domainsRemoved: 'Removed: {{domains}}',
      via: { login: 'from the directory at sign-in', refresh: 'from the directory at renewal' },
      budget: {
        monthly: "The company's monthly budget: {{from}} → {{to}}",
        memberDefault: 'Default per member: {{from}} → {{to}}',
        domain: '{{domain}} default: {{from}} → {{to}}',
        member: '{{name}} · own: {{from}} → {{to}}',
        bulk_one: 'together, {{count}} member',
        bulk_other: 'together, {{count}} members',
        members_one: "{{count}} member's budget",
        members_other: "{{count}} members' budgets",
        noLimit: 'No limit',
        platform: 'Platform',
        organization: 'Company',
        default: 'Default',
      },
      exceeded: { daily: 'Daily budget: {{used}} / {{limit}}', monthly: "The company's monthly budget: {{used}} / {{limit}}" },
      someone: 'A member',
      sessions_one: '{{count}} session ended',
      sessions_other: '{{count}} sessions ended',
      newUsername: 'New user name: {{username}}',
      lastDays_one: 'Last {{count}} day',
      lastDays_other: 'Last {{count}} days',
      rows_one: '{{count}} row',
      rows_other: '{{count}} rows',
      lists: { members: 'Member list', usage_members: "Members' usage", usage_workspaces: "Workspaces' usage" },
      skillFailure: { too_large: 'too large', no_room: 'no room in the context', not_found: 'not found', disabled: 'turned off' },
      demoPeople_one: '{{count}} person',
      demoPeople_other: '{{count}} people',
      demoDomains_one: '{{count}} domain',
      demoDomains_other: '{{count}} domains',
      trialLeft_one: '{{count}} day left',
      trialLeft_other: '{{count}} days left',
      trialEnded: { expired: 'it ran out', license: 'a license started' },
      models: {
        unreachable: 'could not connect',
        found_one: 'connection tried, {{count}} model',
        found_other: 'connection tried, {{count}} models',
        withKey: 'with a key',
        renamed: 'formerly "{{from}}"',
        address: 'address: {{address}}',
        addressChanged: 'address changed',
        addressFrom: 'address: {{from}} → {{address}}',
        unset: 'automatic',
        on: 'on',
        off: 'off',
        keyReplaced: 'key changed',
        keyCleared: 'key deleted',
        turnedOn: 'turned on',
        turnedOff: 'turned off',
        testOk: 'connection works',
        testFailed: 'could not connect',
        testModel: 'model {{model}}',
        refreshed: '{{added}} new, {{missing}} removed models',
        fields: {
          displayName: 'display name',
          enabled: 'on or off',
          contextWindowOverride: 'context window',
          maxOutputTokens: 'longest answer',
          priceInputMicros: 'input cost',
          priceCachedInputMicros: 'cached input cost',
          priceOutputMicros: 'output cost',
          thinking: 'thinking',
          supportsTools: 'tool calls',
          description: 'description',
          kind: 'kind',
          chatOffered: 'in chat',
          chatAudience: 'who may use it',
          teams: 'teams',
        },
        audiences: { everyone: 'Everyone', teams: 'Chosen teams' },
        mainModel: 'Main model',
        none: 'Not assigned',
        internalOn: 'Providers may also reach internal network addresses',
        internalOff: 'Providers may now reach outside addresses only',
      },
      system: {
        backup: { both: 'Database and files', database: 'Database', files: 'Files' },
        kept_one: '{{count}} backup kept',
        kept_other: '{{count}} backups kept',
        backupReason: { disk_full: 'no room on the disk', dump_failed: 'the database could not be backed up', files_failed: 'the files could not be backed up' },
        manual: 'Started by hand',
        first: 'The first backup after the installation',
        firstInstall: 'first installation',
        mailTo: 'to {{to}}',
        mailKind: 'error: {{kind}}',
        installed: '{{version}} installed',
        migrated: 'migrations applied',
        healthy: 'health check passed',
        noAnswer: 'cannot be reached',
        lasted: 'lasted {{duration}}',
        retried_one: '{{count}} request retried',
        retried_other: '{{count}} requests retried',
        failedCalls_one: '{{count}} request not answered',
        failedCalls_other: '{{count}} requests not answered',
      },
    },
  },
  organization: {
    new: {
      title: 'New company',
      hint: 'Open a company for your team; you become its super admin and can invite members. Your personal space stays as it is.',
      needVerified: 'Confirm your e-mail address before you create a company.',
      name: "Company's name",
      submit: 'Create company',
    },
    adminHint: 'You manage {{name}} under Access & permissions: members, invitations and teams.',
    yourRole: 'Your role: {{role}}',
    yourDomains: 'Your domains: {{domains}}',
  },
  models: {
    tabsLabel: 'Model sections',
    tabs: { providers: 'Providers and tasks', capacity: 'Capacity' },
    windowAssumed: 'The provider did not report it; this is an assumption. Correct it in the details.',
    notChecked: 'Not checked yet',
    tools: 'Tool calls',
    thinking: 'Thinking',
    vision: 'Images',
    missing: 'No longer on the provider',
    disabled: 'Off',
    check: 'Check compatibility',
    kinds: { chat: 'Chat', embedding: 'Embedding', rerank: 'Reranking', ocr: 'Document reading', guard: 'Safety' },
    offer: {
      new: 'New',
      displayName: 'Display name: {{name}}',
      duplicate: 'Looks like the same model as {{name}}; do not open both',
      guessed: 'guessed from its name',
      kindOf: '{{name}}: kind',
      kindFixed: 'A task uses it; to change its kind, choose another model for that task first.',
      notChat: 'Not a chat model',
      switchOf: '{{name}}: in chat',
      on: 'On',
      off: 'Off',
      keepPlan: 'It is in a plan. Take it out of the plan in Plans first to remove it from chat.',
      keepTask: 'The Chat task’s model stays open to everyone. Choose another model for the task in Model per task first.',
      windowAssumed: 'Context unknown',
      checking: 'Checking compatibility…',
      audience: 'Who may use it',
      audienceOf: '{{name}}: {{who}}',
      everyone: 'Everyone',
      everyoneHint: 'Every user in the firm',
      chosenTeams: 'Chosen teams',
      chosenTeamsHint: 'Only these teams’ members see it in the picker',
      teamsCount_one: '{{count}} team',
      teamsCount_other: '{{count}} teams',
      people_one: '{{count}} person',
      people_other: '{{count}} people',
      noTeams: 'No teams yet.',
      apply: 'Apply',
      added: '{{name}} added · {{found}} models found ({{added}} new).',
      addedNone: '{{name}} added. Test the connection or refresh its models.',
      addedPlans: 'New models start closed; people do not see them. Open the ones to offer in chat and choose their plans in Plans. Embedding, reranking, document reading and safety models cannot be opened in chat; tasks only use them.',
      noteLead: 'You decide which models can be picked in chat.',
      note: 'Newly added models start closed. Open a model to everyone or only to the teams you choose; a person sees only the models open to them in the picker. Embedding, reranking and safety models cannot be opened in chat.',
    },
    scopes: { platform: 'Platform', tenant: 'Company', user: 'Personal' },
    providerKinds: { openai: 'OpenAI', openai_compatible: 'OpenAI compatible', custom: 'Model added by hand' },
    list: {
      title: 'Providers',
      providers_one: '{{count}} provider',
      providers_other: '{{count}} providers',
      models_one: '{{count}} model',
      models_other: '{{count}} models',
      offered_one: '{{count}} in chat',
      offered_other: '{{count}} in chat',
    },
    table: {
      caption: 'Models of {{name}}',
      model: 'Model',
      kind: 'Type',
      usage: 'Used for',
      window: 'Context',
      chat: 'In chat',
      capabilities: 'Capabilities',
      actions: 'Actions',
      more: '{{name}}: more actions',
      moreTasks_one: '+{{count}} task',
      moreTasks_other: '+{{count}} tasks',
    },
    test: {
      ok: 'The connection works.',
      failed: 'The connection check failed.',
      checks: { models: 'Model list', chat_stream: 'Streaming answers', tool_calls: 'Tool calls', thinking: 'Thinking', tokenize: 'Token counting' },
      warnings: {
        insecure_transport: 'The connection is not encrypted (http). Use https in production.',
        context_window_unknown: "The provider does not report the context window; enter it in the model's details.",
      },
    },
    details: {
      title: 'Details',
      dialogTitle: '{{name}} · details',
      displayName: 'Display name',
      displayNameHint: 'Conversations and lists show this name; when empty, the technical id: {{id}}.',
      description: 'Short description',
      descriptionHint: 'Shown under the name in the model picker (e.g. "balanced, for any work").',
      window: 'Context window (tokens)',
      windowHint: 'When empty, the value the provider reports: {{detected}}.',
      maxOutput: 'Longest answer (tokens)',
      priceIn: 'Cost: 1 million input tokens',
      priceOut: 'Cost: 1 million output tokens',
      priceHint: 'The usage page computes cost with these values; leave 0 for your own server.',
      thinking: 'Thinking',
      tools: 'Tool calls',
      toolsUnknown: 'Unknown',
      toolsYes: 'Supported',
      toolsNo: 'Not supported',
      disable: 'Turn the model off',
      enable: 'Turn the model on',
    },
    thinkingModes: {
      unknown: 'Unknown',
      none: 'Does not think',
      template_toggle: 'Can be turned on and off',
      effort_toggle: 'Thinking effort can be chosen',
      always: 'Always thinks',
    },
    provider: {
      connected_one: 'Connected · {{count}} model',
      connected_other: 'Connected · {{count}} models',
      error: 'Connection error',
      untested: 'Not tested',
      keySaved: 'Key saved',
      noKey: 'No key',
      testedAt: 'Last test {{date}}',
      test: 'Test the connection',
      refresh: 'Refresh models',
      refreshed: '{{added}} new models, {{missing}} removed models.',
      noModels: 'No models yet. Test the connection or refresh the models.',
      more: '{{name}}: more actions',
      edit: 'Edit',
      editTitle: 'Edit the provider',
      enabled: 'Provider on',
      enabledHint: 'while off, no task uses its models.',
      deleteTitle: 'Delete "{{name}}"?',
      deleteBody_one: 'The provider and its {{count}} model are removed; its key is deleted.',
      deleteBody_other: 'The provider and its {{count}} models are removed; its key is deleted.',
      deleteTasks_one: 'This task uses its model: {{tasks}}. Deleted, it stops until another model is chosen.',
      deleteTasks_other: 'These {{count}} tasks use its models: {{tasks}}. Deleted, they stop until another model is chosen.',
      deleteNoTasks: "No task uses this provider's models.",
    },
    add: {
      title: 'Add provider',
      kind: 'Type',
      name: 'Name',
      namePlaceholder: 'Spare GPU',
      baseUrl: 'Address',
      baseUrlHint: 'The OpenAI-compatible server’s address ending in /v1 (vLLM, LiteLLM…).',
      openaiHint: 'Leave it empty for OpenAI’s address. Content sent to external cloud services leaves the company.',
      modelId: 'Model name',
      key: 'API key',
      keyPlaceholder: 'Leave empty when there is no key',
      keyHint: 'Never shown again once saved.',
      newKey: 'New API key',
      newKeyHint: 'The saved key is never shown; the key you type replaces it.',
      clearKey: 'Delete the key',
      clearKeyHint: 'The provider is called without a key.',
      capacityManaged: 'Manage GPU capacity',
      capacityManagedHint: 'Concurrent requests follow the server’s load (vLLM).',
      logOptOut: 'Ask for no content logging',
      logOptOutHint: 'The provider is asked not to log request content (LiteLLM).',
      test: 'Try the connection',
      testOk: 'Connection works.',
      testFound_one: 'Connection works · {{count}} model found: ',
      testFound_other: 'Connection works · {{count}} models found: ',
      submit: 'Add',
    },
    routes: {
      title: 'Model per task',
      caption: {
        installation: 'Which model runs each task.',
        platform: "The platform's choice. Companies may change it with their own models.",
        tenant: "The company's choice; tasks you leave follow the platform's.",
      },
      task: 'Task',
      model: 'Model',
      choose: 'Model for {{task}}',
      none: 'Not assigned',
      mainModel: 'Main model · {{name}}',
      viaHelper: 'Other small jobs’ model · {{name}}',
      above: { platform: "Platform's choice · {{name}}", tenant: "Company's choice · {{name}}" },
      groups: {
        main: { title: 'Main work', hint: 'the answers people see' },
        helpers: { title: 'Helper jobs', hint: 'a smaller model here takes load off the main GPU' },
        search: { title: 'Document search', hint: 'searching documents by meaning' },
      },
      tasks: {
        main: 'Chat',
        code: 'Code agent',
        subagent: 'Sub-agents',
        helper: 'Other small jobs',
        title: 'Titles',
        compaction: 'Conversation summary',
        summary: 'Summaries',
        embedding: 'Document embedding',
        rerank: 'Reranking',
        ocr: 'Document reading (OCR)',
      },
      hints: {
        main: '{{chatProduct}} conversations',
        code: '{{codeProduct}} workspaces',
        subagent: 'helpers the main agent starts',
        helper: 'explanations and the like',
        title: 'conversation titles',
        compaction: 'summing up a long conversation',
        summary: 'document and page summaries',
        embedding: 'searching documents by meaning',
        rerank: 'ordering search results',
        ocr: 'reading text in scanned documents',
      },
    },
  },
  capacity: {
    period: 'Period: {{day}}',
    days: { today: 'Today', yesterday: 'Yesterday' },
    server: {
      title: 'Model server',
      status: { ok: 'Running', errors: 'Running with errors', down: 'Unreachable', idle: 'Idle' },
      calls_one: '{{count}} call, {{errors}} errors in the last 15 min',
      calls_other: '{{count}} calls, {{errors}} errors in the last 15 min',
      table: 'Server',
      gpu: { title: 'GPU memory', hint: 'the {{model}} model' },
      memory: { title: 'Server memory', hint: 'Oppy and other services' },
      disk: { title: 'Disk', database: 'database {{size}}', files: 'files {{size}}', backups: 'backups {{size}}', free: '{{size}} free' },
      queues: { title: 'Waiting jobs', hint: 'chat {{interactive}} · background {{background}} · documents {{documents}}' },
      sandboxes: { title: 'Open code sandboxes', hint: 'chat {{chat}} · {{codeProduct}} {{code}}' },
      backup: {
        title: 'Last backup',
        nightly: 'every night',
        kept_one: 'every night, {{count}} backup kept',
        kept_other: 'every night, {{count}} backups kept',
        none: 'no nightly backup set up yet',
        today: 'Today {{time}}',
        yesterday: 'Yesterday {{time}}',
      },
    },
    users: { title: 'Active users now', limit: 'measured limit {{low}}–{{high}} · last 15 min' },
    code: { title: 'Code jobs running', limit: 'measured limit {{low}}–{{high}}' },
    ttft: {
      title: 'First response time',
      value: '{{value}} s',
      line: 'usually (p50) · slowest 5% (p95) {{p95}} s',
      none: 'no answers in the last 15 minutes',
    },
    verdict: { enough: 'Capacity is enough.', close: 'Capacity is getting close to the limit.', over: 'Capacity is exceeded.' },
    rule: 'If active users often pass {{low}} or the first response time (p95) passes 5 seconds, consider a second GPU or a smaller helper model.',
    chart: {
      title: { today: 'Active users by hour today', yesterday: 'Active users by hour yesterday' },
      peak: 'Busiest: {{hour}}, {{count}} people',
      empty: 'No model calls on this day',
      band: 'Measured limit: {{low}}–{{high}} active users',
      people_one: '{{count}} person',
      people_other: '{{count}} people',
      hourValue_one: '{{hour}}: {{count}} person',
      hourValue_other: '{{hour}}: {{count}} people',
    },
  },
  platform: {
    intro: 'The providers, models, capacity and platform audit log shared by every company.',
    tabsLabel: 'Platform sections',
    title: 'Platform',
    onlyOperators: 'Only platform operators can open this page.',
    tabs: { providers: 'Providers and models', tenants: 'Companies', capacity: 'Capacity', audit: 'Audit log' },
    tenants: {
      title: 'Companies',
      hint: 'Provider addresses on the internal network are open only to the companies you tick; cloud metadata and platform services are always closed.',
      name: 'Company',
      members: 'Members',
      tokens: 'Tokens, last 30 days',
      search: 'Document search',
      internal: 'Internal addresses',
      allowInternal: 'Let {{name}} use internal network addresses',
      searchModes: { hybrid: 'Words + meaning', keyword: 'Words only' },
    },
    audit: { title: 'Platform audit log' },
  },
  policies: {
    active: 'Active',
    approvals: { title: 'Approvals and access' },
    flags: {
      requireCodeApproval: { title: 'Approval before code runs in chats', hint: 'when on, {{chatProduct}} asks before running code.' },
      allowFullAccess: { title: 'Allow full access mode', hint: 'working without approvals in trusted code workspaces.' },
      memoryAllowed: { title: 'Memory', hint: 'when off, Oppy remembers nothing for anyone.' },
      allowPersonalProviders: { title: 'Members may add their own model providers', hint: 'with their own keys, for themselves only.' },
    },
    documentSearch: {
      title: 'Document search',
      hybrid: {
        title: 'Words + meaning',
        hint: 'Passages of documents are sent to a service that turns their meaning into numbers (Bulutistan). The right place is found even when the question uses other words.',
      },
      keyword: {
        title: 'Words only',
        hint: 'Document text never leaves our servers. Search uses the words of the question; it finds less when the question uses synonyms.',
      },
      confirm: 'Switching to words only deletes the stored meaning index at once. If you switch back, documents are indexed again in the background.',
      switch: 'Switch to words only',
    },
    documentVectors: {
      paused: { title: 'Documents cannot get their meaning index' },
      documents: {
        title_one: '{{count}} document did not get its meaning index',
        title_other: '{{count}} documents did not get their meaning index',
      },
      problem: '{{problem}} ({{detail}}).',
      problemPlain: '{{problem}}.',
      pausedHint: 'Meanwhile documents are searched by words; meaning search comes back by itself once the service answers.',
      waiting_one: '{{count}} document is waiting.',
      waiting_other: '{{count}} documents are waiting.',
      documentsHint: 'Each is tried again on its own schedule; meanwhile they can be searched by words.',
      attempts: 'Last attempt {{last}} · next {{next}}',
      attemptsSince_one: 'Last attempt {{last}} · next {{next}} · {{count}} attempt, first failure {{since}}',
      attemptsSince_other: 'Last attempt {{last}} · next {{next}} · {{count}} attempts, first failure {{since}}',
      probing: 'Last attempt {{last}} · trying again with one document now',
      soon: 'shortly',
      indexing_one: '{{count}} document is being indexed for meaning.',
      indexing_other: '{{count}} documents are being indexed for meaning.',
      retry: 'Try again now',
      retried: 'Waiting documents will be tried again within a minute.',
      problems: {
        not_answering: 'The embedding service is not answering',
        unreachable: 'The embedding service cannot be reached',
        auth: 'The embedding service does not accept the key',
        model: 'The embedding model cannot be found',
        no_model: 'No model is chosen for document embedding',
        quota: 'A usage limit is reached',
        blocked: "The embedding service's address is blocked",
        gateway: "Oppy's model gateway cannot be reached",
        rejected: 'The embedding service does not accept the text of these documents',
        invalid: 'The embedding service sends invalid replies',
        other: 'The embedding service is failing',
      },
    },
    models: {
      title: 'Chat models',
      moved: 'Which models members can pick in chat, and who may use each, is now set in Admin › Models with each model’s “In chat” and “Who may use it”. The Chat task’s model is open to everyone.',
      open: 'Go to Models',
    },
    intro: "The company's rules for approvals, document search and the web; chat models are under Models.",
  },
  audit: {
    hint: 'Administration and security events. Conversation content never appears here.',
    filter: 'Show',
    filters: { all: 'All', tenant: 'Company and members', quota: 'Budgets', mcp: 'Connections', platform: 'Platform', audit: 'Exports' },
    export: 'Download as CSV (last 90 days)',
    empty: 'No entries.',
    system: 'System',
    more: 'Older entries',
    columns: { time: 'Time', action: 'Event', actor: 'Who', details: 'Details' },
    actions: {
      tenant_created: 'Company created',
      tenant_renamed: 'Company renamed',
      tenant_full_access_changed: 'Full access permission changed',
      tenant_settings_changed: 'Company settings changed',
      tenant_member_role_changed: "Member's role changed",
      tenant_member_removed: 'Member removed',
      tenant_member_left: 'Member left',
      tenant_invite_created: 'Invitation sent',
      tenant_invite_accepted: 'Invitation accepted',
      tenant_document_search_changed: 'Document search changed',
      tenant_document_vectors_retried: 'Meaning index tried again',
      tenant_models_allowed_changed: 'Allowed models changed',
      tenant_personal_providers_changed: 'Personal provider permission changed',
      quota_exceeded: 'Token budget used up',
      quota_changed: 'Budgets changed',
      audit_exported: 'Audit log downloaded',
      mcp_oauth_client_set: "Connection's sign-in client saved",
      mcp_oauth_client_cleared: "Connection's sign-in client removed",
      platform_internal_providers_changed: 'Internal address permission changed',
      platform_provider_created: 'Platform provider added',
      platform_provider_updated: 'Platform provider changed',
      platform_provider_deleted: 'Platform provider deleted',
      platform_models_refreshed: 'Platform models refreshed',
      platform_model_updated: 'Platform model changed',
      platform_route_changed: "Platform's task assignment changed",
      approval_decided: 'Approval card answered',
      approval_mode_changed: 'Approval mode changed',
      auth_provider_created: 'Company sign-in service connected',
      auth_provider_changed: 'Company sign-in service settings changed',
      auth_link_requested: 'Local account armed to join the company sign-in',
      auth_account_linked: 'Local account joined the company sign-in',
      tenant_converted: 'Personal space turned into a company',
      auth_login_failed: 'Sign-in failed',
      auth_break_glass_login: 'Break-glass admin signed in',
      auth_break_glass_set: 'Break-glass admin set up',
      auth_refresh_refused: 'Company sign-in not renewed, session ended',
      auth_role_changed: 'Directory role changed',
      auth_domains_changed: 'Directory domains changed',
      tenant_member_deactivated: 'Member’s access turned off',
      tenant_member_reactivated: 'Member’s access turned on',
      identity_domain_renamed: 'Domain workspace renamed',
      auth_login: 'Signed in',
      auth_logout: 'Signed out',
      auth_password_changed: 'Password changed',
      document_deleted: 'Document deleted',
      document_shared: 'Document shared',
      document_uploaded: 'Document uploaded',
      mcp_server_created: 'Connection added',
      mcp_server_deleted: 'Connection deleted',
      mcp_server_updated: 'Connection changed',
      mcp_server_trusted: 'Connection trusted',
      mcp_server_untrusted: 'Connection no longer trusted',
      permission_rule_revoked: 'Permission rule removed',
      skill_created: 'Skill added',
      skill_proposed: 'Skill proposed',
      skill_published: 'Skill published',
      skill_rejected: 'Skill rejected',
      skill_deleted: 'Skill deleted',
      skill_rolled_back: 'Skill rolled back',
      skill_version_added: 'New skill version',
      team_created: 'Team created',
      team_deleted: 'Team deleted',
      tenant_web_policy_changed: 'Web policy changed',
      user_data_deleted: 'User deleted their data',
      workspace_created: 'Workspace created',
      workspace_deleted: 'Workspace deleted',
    },
  },
  skillAnalytics: {
    hint: 'Which skills are used, whether the model picked them or they were called by name, failed loads and cost. No conversation content.',
    used: 'Skills used',
    empty: 'No skills were used in this period.',
    unused: 'Everyone and Team skills nobody used',
    noneUnused: 'No unused skills.',
    costHint: "Tokens the skill added to the context / total tokens of the runs that loaded it",
    columns: {
      skill: 'Skill',
      loads: 'Loads',
      byModel: 'Picked by the model',
      byUser: 'Called by name',
      failed: 'Failed',
      users: 'People',
      cost: 'Tokens (skill / run)',
      lastUsed: 'Last used',
    },
    private: { user: "Members' personal skills", workspace: 'Workspace skills' },
    levels: { builtin: 'Standard', organization: 'Everyone', team: 'Team', user: 'Personal', workspace: 'Workspaces' },
  },
  integrations: {
    hint: 'The connections added for everyone. To add or remove them:',
    manage: 'Connections page',
    empty: 'The company has no connections yet.',
    modes: { available: 'Optional', default_on: 'On by default', required: 'Required' },
    auth: { none: 'No sign-in', headers: 'With a key', oauth: 'Account sign-in (OAuth)' },
    tools_one: '{{count}} tool',
    tools_other: '{{count}} tools',
    oauth: {
      saved: 'Saved sign-in client: {{clientId}}',
      none: 'If the sign-in service does not register clients itself, enter the client you registered with it beforehand.',
      secretSaved: 'Secret saved',
      done: 'Sign-in client saved. Members use it at their next sign-in.',
      clientId: 'Client ID',
      secret: 'Client secret',
      secretHint: "Visible only now; never shown once saved. Leave empty if you don't change it.",
      clear: 'Remove the client',
    },
  },
  notFound: { title: 'Page not found', body: 'The address may be wrong, or the page was removed.', back: 'Back to home' },
  // v0.15a.3: a page or a panel that could not be drawn
  crash: {
    title: 'Something went wrong',
    body: 'The page stopped on an unexpected error. Reloading usually fixes it.',
    panelBody: 'This panel could not open; the conversation goes on.',
    reload: 'Reload',
    home: 'Back to home',
  },
  fields: {
    invalid: 'Check this field.',
    email: 'Enter a valid email address.',
    required: 'This field is required.',
  },
  errors: {
    ...m365ErrorsEn,
    document_timeout: {
      title: 'The document was not read in time; the answer did not start. Try again once it is ready.',
      action: 'A large or scanned document can take long to read.',
    },
    document_unreadable: {
      title: 'An attached document could not be read; the answer did not start. Check the file and attach it again.',
      action: 'Make sure the document is not damaged or password protected.',
    },
    mcp_test_failed: { title: 'Could not connect to the connection.', action: 'Check the address, the command or the key and try again.' },
    mcp_needs_auth: { title: 'You need to sign in to this connection.', action: 'Use "Sign in" on the Connections page.' },
    mcp_not_approved: { title: 'This shared connection waits for your approval.', action: 'Review and approve its configuration on the Connections page.' },
    mcp_unavailable: { title: 'The connection cannot be reached right now.', action: 'Try again in a moment; if it continues, refresh the connection.' },
    mcp_user_servers_disabled: { title: 'Your company turned adding your own connections off.', action: 'Use the connections in the Everyone section or ask your admin.' },
    name_taken: { title: 'This name is already used at this level.', action: 'Choose another name or update the existing skill.' },
    skill_invalid: { title: 'The skill file could not be read.', action: 'Check that the frontmatter has a name and a description and that the instructions are not too long.' },
    skill_source_unavailable: { title: 'The skill source could not be reached.', action: 'Check the address; for a private repository upload a ZIP instead.' },
    skill_external_disabled: { title: 'Your company turned installing skills from outside sources off.', action: 'Use the skills in the Everyone section or ask your admin.' },
    skill_choose_folder: { title: 'This source holds several skills.', action: 'Choose the folder to install from the list.' },
    skill_too_large: { title: 'This skill is too big.', action: 'Remove the sample files and upload it as a ZIP.' },
    skill_source_rate_limited: { title: 'GitHub is making us wait.', action: 'Try again a little later or upload the skill as a ZIP.' },
    skill_source_not_allowed: { title: 'Connecting to this address is not allowed.', action: 'Your admin can add it to the allowed sites. Meanwhile you can upload the skill as a ZIP.' },
    skill_source_not_found: { title: 'This address was not found.', action: 'Check the address; for a private repository zip the skill folder and upload it.' },
    skill_source_timeout: { title: 'The source did not answer in time.', action: 'Try again in a few minutes.' },
    skill_source_network: { title: 'The source could not be reached.', action: 'If it goes on, give your admin the error number.' },
    skill_no_skill_md: { title: 'No skill was found at this address.', action: 'Paste the link to the skill’s folder.' },
    workspace_untrusted: { title: 'This workspace is not trusted.', action: 'Use a trusted workspace for local connections and skill permissions.' },
    search_unavailable: { title: 'Web search is not available right now.', action: 'Try again in a moment or give a known address directly.' },
    validation_failed: { title: 'Some fields are missing or wrong.', action: 'Fix the marked fields and try again.' },
    unauthenticated: { title: 'Your session has ended.', action: 'Sign in again.' },
    forbidden: { title: 'You are not allowed to do this.', action: 'If needed, ask whoever created the workspace or an admin for access.' },
    not_found: {
      title: 'We could not find what you are looking for.',
      action: 'The link may be outdated or you may not have access. Go back to the dashboard.',
    },
    conflict: { title: 'This action conflicts with the current state.', action: 'Refresh the page and try again.' },
    csrf_failed: { title: 'A security check failed.', action: 'Refresh the page and try again.' },
    client_update_required: { title: 'A newer version of the Oppy app is needed.', action: 'Update the app from TestFlight.' },
    client_unknown: { title: 'This request did not come from the Oppy app.', action: 'Open the app again.' },
    rate_limited: { title: 'Too many attempts.', action: 'Wait a little and try again.' },
    invalid_credentials: {
      title: 'Email or password is wrong.',
      action: 'Check your details. If you forgot your password, you can reset it.',
    },
    directory_unavailable: { title: 'The sign-in service can’t be reached right now.', action: 'Please try again in a few minutes.' },
    account_deactivated: { title: 'Your access to Oppy is turned off.', action: 'Please ask your administrator.' },
    account_relink_required: {
      title: 'Your company account has changed.',
      action: 'You can sign in to the same account once an administrator confirms it; please tell your administrator.',
    },
    membership_from_directory: {
      title: 'Membership comes from the company directory.',
      action: 'Turn the person’s access off instead of removing them; teams are the domains.',
    },
    email_not_verified: {
      title: 'Confirm your email address first.',
      action: 'Open the link in your inbox or request a new one.',
    },
    token_invalid: { title: 'The link is invalid, used or expired.', action: 'Request a new link.' },
    password_too_short: { title: 'The password is too short.', action: 'Choose a password with at least 10 characters.' },
    password_too_long: { title: 'The password is too long.', action: 'Choose a password with at most 256 characters.' },
    password_breached: {
      title: 'This password is too common or appears in breached-password lists.',
      action: 'Choose another password that is hard to guess.',
    },
    invite_email_mismatch: {
      title: 'This invitation was sent to another email address.',
      action: 'Sign in with the address the invitation was sent to.',
    },
    already_member: { title: 'This person is already on the team.', action: 'Check the member list.' },
    last_owner: { title: 'The company needs at least one super admin.', action: 'Make another member a super admin first.' },
    role_not_allowed: { title: 'Only a super admin can give this role.', action: 'Ask a super admin for help.' },
    project_path_invalid: {
      title: 'The folder name is not valid.',
      action: 'Use letters, digits, dots, dashes and underscores; do not use “..” or a leading “/”.',
    },
    project_path_nested: {
      title: 'This folder is inside another project or contains one.',
      action: 'Choose a folder that does not nest.',
    },
    project_path_taken: { title: 'Another project already uses this folder.', action: 'Choose another folder.' },
    workspace_kind_mismatch: {
      title: 'This option is not available in chat spaces.',
      action: 'Folders and repositories are only available in code workspaces.',
    },
    payload_too_large: { title: 'The content is too large.', action: 'Shorten the text and try again.' },
    unsupported_document: {
      title: 'This file type cannot be read as a document.',
      action: 'Upload a PDF, Word, Excel, PowerPoint, text, Markdown, CSV file or a picture (PNG, JPEG).',
    },
    not_ready: { title: 'The document is not ready yet.', action: 'Try again when reading is finished; the status shows next to the document.' },
    provider_url_invalid: { title: 'The provider address is not valid.', action: 'Enter an address that starts with http:// or https://, without a user name or parameters.' },
    provider_url_blocked: {
      title: 'Connecting to this address is not allowed.',
      action: 'Use a provider address that is reachable on the internet. If you need a server on an internal network, contact the platform operator.',
    },
    api_key_required: { title: 'This provider needs an API key.', action: 'Enter the key from your provider and try again.' },
    personal_providers_disabled: {
      title: 'Your company does not allow personal model providers.',
      action: 'Use the models your company provides, or ask an admin for permission.',
    },
    model_unavailable: {
      title: 'The model chosen for this task is not available right now.',
      action: 'It may have been turned off or removed from the provider. Choose another model or tell your admin.',
    },
    service_unavailable: { title: 'The service is not responding right now.', action: 'Try again in a moment.' },
    no_model_configured: { title: 'No model is set up for this workspace.', action: 'Ask your administrator to add a model.' },
    context_overflow: { title: 'The conversation is longer than the model can take.', action: 'Summarise the conversation (/compress) or continue in a new chat.' },
    quota_exceeded: { title: 'Your usage quota is used up.', action: 'Wait a little or talk to your administrator.' },
    model_not_allowed: { title: 'This model is not available in your company.', action: 'Pick another model or ask your administrator to allow this one.' },
    organization_limit: { title: 'You have reached the number of companies you can create.', action: 'Contact support.' },
    file_too_large: { title: 'This file is over your plan’s file size limit.', action: 'Choose a smaller file.' },
    document_limit: { title: 'You reached the document limit.', action: 'Delete one of your documents.' },
    plan_required: { title: 'This is not on your plan.', action: 'It opens on Pro.' },
    payments_closed: { title: 'Payments are not open yet.', action: 'They open here very soon; until then you can go on with the Free plan.' },
    skill_limit: { title: 'You have reached your plan’s limit of own skills.', action: 'Delete one to add another, or move to Pro; the skills that come with Oppy do not count.' },
    connection_limit: { title: 'You have reached your plan’s limit of own connections.', action: 'Remove one to add another, or move to Pro.' },
    model_in_plan: { title: 'This model is in a plan.', action: 'Take it out of the plan in Plans first, then remove it from chat or change its kind.' },
    model_in_task: { title: 'This model is used by a task.', action: 'Choose another model for that task in Model per task first.' },
    model_kept: { title: 'Subscribers still have this model.', action: 'A model taken out of Pro can leave chat once the change announced to subscribers applies.' },
    provider_in_plan: { title: 'A model of this provider is in a plan or still with subscribers.', action: 'Take these models out of the plans in Plans first; subscribers keep them until the announced change applies. Then delete the provider.' },
    already_subscribed: { title: 'You already have a Pro subscription.', action: 'You can manage it in Settings › Subscription.' },
    legal_version_changed: { title: 'The contract texts changed after this page was opened.', action: 'Reload the page and accept the texts again.' },
    scope_not_available: { title: 'Here there is only your own space.', action: 'Save it as personal.' },
    code_invalid: { title: 'The code is not right.', action: 'Check the code and enter it again.' },
    code_expired: { title: 'The code expired.', action: 'Ask for a new code.' },
    registration_unknown: { title: 'This sign-up is no longer valid.', action: 'Create your account again.' },
    provider_unavailable: { title: 'The model provider cannot be reached right now.', action: 'Try again in a moment.' },
    no_visible_reply: { title: 'The model did not produce an answer.', action: 'Send your message again or try a shorter request.' },
    run_interrupted: { title: 'The run was interrupted.', action: 'Resume it with the continue button.' },
    queue_full: { title: 'Too many messages are waiting.', action: 'Wait until the earlier messages are handled.' },
    run_not_active: { title: 'This run is no longer active.', action: 'Refresh the page.' },
    approval_not_pending: { title: 'This request was already answered.', action: 'Refresh the page.' },
    turn_active: { title: 'An answer is being prepared in this conversation.', action: 'Try again when it finishes, or stop it first.' },
    rewind_before_compaction: { title: 'This point is inside the summarised part, so you cannot go back to it.', action: 'Pick a message after the summary, or branch from this point.' },
    nothing_to_compact: { title: 'The conversation is not long enough to summarise.', action: 'Summarising happens by itself when the conversation grows.' },
    compaction_failed: { title: 'The conversation could not be summarised.', action: 'Try again in a moment; if it keeps failing, continue in a new chat.' },
    nothing_to_regenerate: { title: 'There is no answer to regenerate.', action: 'Send a message first.' },
    memory_disabled: { title: 'Memory is off.', action: 'Turn it on in Settings > Memory; if your company admin turned it off, ask them.' },
    mode_not_allowed: { title: 'This approval mode is not available here.', action: 'Choose another mode.' },
    sandbox_unavailable: { title: 'The workspace environment could not start right now.', action: 'Try again in a moment.' },
    sandbox_limit: { title: 'You have reached the limit of environments running at once.', action: 'Close a workspace you are not using, or wait a little.' },
    sandbox_error: { title: 'The operation could not finish in the environment.', action: 'Check the details and try again.' },
    path_outside_workspace: { title: 'This location is outside the workspace.', action: 'Choose a file inside the workspace.' },
    path_not_found: { title: 'The file or folder was not found.', action: 'Refresh the list and try again.' },
    read_only: { title: 'This file is read-only.', action: 'Copy it into the workspace to change it.' },
    is_directory: { title: 'This is a folder.', action: 'Choose a file.' },
    not_a_directory: { title: 'This is not a folder.', action: 'Choose a folder.' },
    sensitive_path: { title: 'This file may contain secrets.', action: 'Opening it needs your separate approval.' },
    disk_quota_exceeded: { title: 'The workspace disk is full.', action: 'Delete files you do not need and try again.' },
    not_a_git_repo: { title: 'This workspace has no Git repository.', action: 'Create a Git repository first.' },
    git_error: { title: 'The Git operation could not finish.', action: 'Check the details and try again.' },
    port_not_listening: { title: 'No application is running on this port.', action: 'Start the application and try again.' },
    preview_unreachable: { title: 'The preview could not open.', action: 'Make sure the application is running and try again.' },
    preview_unavailable: { title: 'Preview is off on this installation.', action: 'It opens once a domain name is connected; your files are in the Files tab.' },
    snapshot_not_found: { title: 'This restore point is no longer valid.', action: 'Refresh the list and choose another point.' },
    snapshot_failed: { title: 'The restore point operation could not finish.', action: 'Try again in a moment.' },
    internal_error: {
      title: 'Something unexpected went wrong.',
      action: 'Try again. If it keeps happening, send the request ID from the technical details to support.',
    },
    network_error: { title: 'The server could not be reached.', action: 'Check your internet connection and try again.' },
    license_read_only: {
      title: 'Oppy is read-only: the license has ended.',
      action: 'Old conversations and documents open and can be exported; nothing new can start. Please tell your admin.',
    },
    product_not_licensed: { title: 'This product is not in your license.', action: 'Ask your admin to have it added to the license.' },
    retryAfter: 'You can try again in {{minutes}} minutes.',
  },
  // SA-023: which budget is used up and when it resets; a run that stopped for it (D-97, KotaSohbet.dc.html)
  quotaNotice: {
    user_daily: 'Your daily token budget is used up.',
    tenant_monthly: "Your company's monthly token budget is used up.",
    resets: 'It resets at <b>{{when}}</b>.',
    adminCanRaise: 'Your administrator can raise the limit.',
    kept: {
      code: 'The files written so far stay in the workspace; the work continues where it stopped once the budget resets or is raised.',
      chat: 'What was prepared so far stays in this conversation; the work continues where it stopped once the budget resets or is raised.',
    },
    usedToday: 'Today: {{used}} / {{limit}} prompt tokens',
    usedMonth: 'This month: {{used}} / {{limit}} tokens',
    still: 'The budget is still used up; try again after it resets or once it is raised.',
    limits: 'Limits and quotas',
  },
  // Phase 13f: the enterprise license (06 §26.4; YonetimLisans, LisansYukle, LisansRozet, LisansSaltOkunur, LisansBildirim)
  license: {
    openPage: 'Open the License page',
    chip: {
      days_one: 'License: {{count}} day',
      days_other: 'License: {{count}} days',
      trial_one: 'Trial: {{count}} day',
      trial_other: 'Trial: {{count}} days',
      readOnly: 'Read-only',
      hint: 'License status',
    },
    popover: {
      titles: { reminder: 'The license ends soon', grace: 'License in its grace period', readOnly: 'Oppy is read-only', trial: 'Trial version' },
      reminderBody: 'The license ends on {{day}}. Talk to {{vendor}} to renew it.',
      trialBody: 'Oppy works fully and free of charge until {{day}}.',
      trialEndedBody: 'The free trial ended on {{day}}.',
      graceBody_one: 'The license ended on {{day}}. Oppy works normally for {{count}} more day.',
      graceBody_other: 'The license ended on {{day}}. Oppy works normally for {{count}} more days.',
      readOnlyBody: 'The license ended on {{day}} and the grace period is over.',
      noLicenseBody: 'This installation has no valid license.',
      steps: { reminder: 'Reminder', grace: 'Grace period', readOnly: 'Read-only', trial: 'Trial', license: 'License' },
      now: 'Now',
      from: 'From {{day}}',
      reminderNote: 'After the end there are 14 days of grace; then Oppy becomes read-only. Your data is not deleted.',
      trialNote: 'To carry on, send the installation ID to {{vendor}} and upload the license file you receive. Your data is never deleted.',
      trialNoteQueued: 'The license you uploaded starts by itself on {{day}}. Your data is never deleted.',
      graceNote: 'From {{day}} old conversations open but no new messages can be sent. Your data is not deleted. Please tell your admin.',
      graceNoteAdmin: 'From {{day}} old conversations open but no new messages can be sent. Your data is not deleted. Upload the new license file on the License page.',
      readOnlyNote: 'You can open and export your old conversations and documents; no new messages can be sent. Your data was not deleted. Please tell your admin.',
      readOnlyNoteAdmin: 'Old conversations and documents open and can be exported; no new messages can be sent. A new license file brings everything back at once.',
    },
    facts: {
      owner: 'Licensed to',
      products: 'Products',
      validity: 'Valid',
      licenseId: 'License no.',
      installation: 'Installation ID',
      users: 'Users',
      usersFull: 'Unlimited · company license',
      usersTrial: 'Unlimited · trial license',
    },
    page: {
      plainHttp: 'Connection not encrypted (http)',
      plainHttpHint: 'Until a domain with HTTPS, passwords and sessions cross the network unencrypted.',
      intro: "This installation's Oppy license. Only super admins see this page.",
      status: { active: 'License active', reminder: 'The license ends soon', grace: 'License in its grace period', read_only: 'Oppy is read-only', none: 'No license', trial: 'Trial version', trialEnded: 'The trial has ended' },
      trialSub: '30-day free trial · both products on · no license uploaded yet',
      trialSubQueued: '30-day free trial · both products on',
      trialEndedLine: 'Oppy is read-only. To carry on, send the installation ID to {{vendor}} and upload the license file you receive; everything opens again at once.',
      installed: 'Installed: {{day}}',
      trialEnd: 'Trial ends: {{day}}',
      trialTermLabel: '{{percent}}% of the trial has passed',
      trialNext: 'The next license, {{id}}, starts by itself on {{day}}; the trial ends that day.',
      trialNextGap: 'The next license, {{id}}, starts by itself on {{day}}. The trial ends on {{ends}}; Oppy is read-only in between.',
      kindFull: 'Company license · no user limit',
      kindTrial: 'Trial license · no user limit',
      renew: 'Talk to {{vendor}} to renew it, then upload the new license file here.',
      graceLine: 'The license ended on {{ended}}; everything still works. Without a new license Oppy becomes read-only on {{day}}.',
      readOnlyLine: 'No new messages or code runs; old conversations and documents open. A new license file brings everything back at once.',
      noneLine: 'This installation has no valid license, so Oppy is read-only. Send the installation ID below to {{vendor}} and ask for a license file.',
      days_one: '{{count}} day',
      days_other: '{{count}} days',
      left: 'left · ends {{day}}',
      graceLeft: 'of grace left · read-only from {{day}}',
      start: 'Start: {{day}}',
      end: 'End: {{day}}',
      termLabel: '{{percent}}% of the license term has passed',
      next: 'The next license, {{id}}, starts by itself on {{day}}.',
      details: 'License details',
      copy: 'Copy',
      copied: 'Copied',
      boundNote: 'The license is bound to this installation; copied to another server it does not work. Send this ID to {{vendor}} when you renew or ask for a new license.',
      continue: {
        title: 'To carry on after the trial',
        copyTitle: 'Copy the installation ID',
        copyText: "This installation's ID. Send it to {{vendor}}.",
        prepareTitle: '{{vendor}} prepares your license',
        prepareText: '{{vendor}} prepares a license file for this ID.',
        uploadTitle: 'Upload the license file',
        uploadText: 'Upload the file you receive with <b>Upload license file</b> at the top right. Once it is uploaded the trial ends and the license starts.',
      },
      trialEnds: {
        title: 'What happens when the trial ends?',
        firstTitle: 'The first 30 days',
        first: 'Everything is on. Only super admins see the days left in the top bar.',
        lastTitle: 'The last 7 days',
        last: 'Super admins are notified 7, 3 and 1 day before; the chip turns amber.',
        readOnlyTitle: 'Read-only after the 30th day',
        readOnly: 'No grace period. Old conversations and documents open; no new messages can be sent. A license opens everything again at once.',
        foot: 'Each installation gets one trial; it cannot be restarted once it ends.',
      },
      ends: {
        title: 'What happens when it ends?',
        noDelete: 'No data is ever deleted',
        reminderTitle: '30 days before the end',
        reminder: 'Super admins are notified 30, 7 and 1 day before; the days left show in the top bar. Members see nothing.',
        graceTitle: '14 days of grace after the end',
        grace: 'Everything keeps working. Everyone is notified once and sees the days left in the top bar.',
        readOnlyTitle: 'Read-only after the grace period',
        readOnly: 'Old conversations and documents open and can be exported. No new messages, no code runs.',
        foot: 'A new license file brings everything back at once. No internet connection is needed.',
      },
      history: {
        title: 'License history',
        uploaded: 'Uploaded',
        by: 'Uploaded by',
        started: 'Started',
        startedBy: 'Started by',
        tool: 'Installer',
        state: 'Status',
        trial: 'trial',
        freeTrial: '30-day trial',
        states: { trial: 'Trial', active: 'Active', queued: 'Queued', ended: 'Ended', invalid: 'Invalid' },
        trialEnded: 'Ended',
        empty: 'No license uploaded yet.',
      },
    },
    upload: {
      title: 'Upload license file',
      drop: 'Choose the .oppy-lisans file {{vendor}} sent you, or drop it here.',
      pick: 'Choose file',
      pickAgain: 'Choose another file',
      picked: '{{size}} · just chosen',
      checking: 'Checking the file…',
      signatureOk: 'Signature valid: signed by {{vendor}} and unchanged',
      installationOk: 'For this installation: installation ID',
      problems: {
        format: 'This is not an Oppy license file.',
        signature: 'The file was changed or has no {{vendor}} signature.',
        installation: 'This file is for another installation.',
        expired: 'This license has expired.',
      },
      installationHint: "This installation's ID is {{id}}. Ask {{vendor}} for a license for this ID.",
      follows: 'This license starts by itself on <b>{{day}}</b>, when the current one ends. There is no gap.',
      startsOn: 'This license starts by itself on <b>{{day}}</b>.',
      trialNow: 'Once it is uploaded the trial ends and this license starts at once.',
      trialFollows: 'This license starts by itself on <b>{{day}}</b>, when the trial ends. There is no gap.',
      trialCut: 'This license starts by itself on <b>{{day}}</b>; the trial ends that day.',
      trialGap: 'This license starts by itself on <b>{{day}}</b>. The trial ends on {{ends}}; Oppy is read-only in between.',
      installed: 'This license is already installed.',
      apply: 'Upload license',
      applied: 'License {{id}} applied.',
    },
    readOnly: {
      title: 'Oppy is read-only now',
      body: 'The license has ended. You can open and export your old conversations and documents; no new messages can be sent. Your data was not deleted, and once the license is renewed you carry on where you left off.',
      bodyNone: 'This installation has no valid license. You can open and export your old conversations and documents; no new messages can be sent. Your data was not deleted.',
      bodyTrial: 'The free trial has run out. You can open and export your old conversations and documents; no new messages can be sent. Your data was not deleted, and once a license is installed you carry on where you left off.',
      tellAdmin: 'Please tell your admin.',
      export: 'Export conversation',
    },
    productOff: {
      title: 'This product is not in your license',
      body: "{{product}} is not in this installation's license. If you want to use it, tell your admin.",
      bodyAdmin: "{{product}} is not in this installation's license. Talk to {{vendor}} to add it; you can upload the new license file here too.",
    },
  },
  microsoft365: { admin: m365AdminEn, user: m365UserEn, chat: m365ChatEn, files: m365FilesEn },
};
